import React, { useState, useEffect, useMemo, useCallback } from 'react';
import AdminLayoutWrapper from '../../components/layout/AdminLayoutWrapper';
import DataTableLayout from '../../components/common/DataTableLayout';
import FilterPanel from '../../components/common/FilterPanel';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Autocomplete,
  Collapse,
  Checkbox,
  ListItemText,
  List,
  ListItem,
  Paper
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import {
  GridToolbarColumnsButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
} from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { useNavigate } from 'react-router-dom';
import { subjectTopicService } from '../../services/subjectTopicService';
import api from '../../services/api';
import { createFilterChangeHandler, clearFilters, dataGridProps } from '../../utils/tableUtils';
import {
  SUBJECTS,
  QUESTIONS_FILTER,
  QUESTIONS_COUNT,
  SUBJECT_TOPICS,
  BOARDS,
  ACADEMIC_YEARS,
  ACADEMIC_YEAR_BOARD_SUBJECT_TOPICS
} from '../../config/Config';
import { filterPublished } from '../../utils/publishedFilter';

const createCurriculumChapter = () => ({
  id: `chapter-${Date.now()}-${Math.random().toString(36).slice(2)}`,
  parentTopic: null,
  subTopics: [],
});

const createEmptyForm = () => ({
  subject: '',
  board: '',
  academic_year: '',
  curriculumChapters: [createCurriculumChapter()],
});

const getTopicParentId = (topic) => {
  const parent = topic?.parenttopic ?? topic?.parent_topic ?? topic?.parentTopic;
  if (!parent) return null;
  return typeof parent === 'object' ? parent.id : parent;
};

const isChildTopic = (topic) => topic?.topic_type === 'child' || Boolean(getTopicParentId(topic));

const isParentTopic = (topic) => !isChildTopic(topic);

const readListPayload = (data) => (
  Array.isArray(data) ? data : (data?.results || [])
);

const fetchAllPaginated = async (initialUrl) => {
  const firstResponse = await api.get(initialUrl);
  let items = readListPayload(firstResponse.data);
  let nextUrl = firstResponse.data?.next;

  while (nextUrl) {
    const nextResponse = await api.get(nextUrl);
    items = items.concat(readListPayload(nextResponse.data));
    nextUrl = nextResponse.data?.next;
  }

  return items;
};

const moveArrayItem = (items, fromIndex, toIndex) => {
  if (toIndex < 0 || toIndex >= items.length) return items;
  const next = [...items];
  const [item] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, item);
  return next;
};

export default function SubjectTopicPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [boards, setBoards] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [associations, setAssociations] = useState([]); // AcademicYearBoardSubjectTopic associations
  const [loading, setLoading] = useState(true);
  const [dialogLevel, setDialogLevel] = useState(null); // 'subject', 'board', 'academic_year', 'parent_topic', 'child_topic'
  const [fixedValues, setFixedValues] = useState({}); // Store fixed values for the dialog
  const [filters, setFilters] = useState({ board: '', academic_year: '', subject: '', topic: '', parenttopic: '', questionCount: '' });
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [curriculumDialogExpanded, setCurriculumDialogExpanded] = useState(false);
  const [form, setForm] = useState(createEmptyForm);
  const [editingId, setEditingId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [showNewSubjectField, setShowNewSubjectField] = useState(false);
  const [newSubjectName, setNewSubjectName] = useState('');
  const [isFormDisabled, setIsFormDisabled] = useState(false);
  const [questionCounts, setQuestionCounts] = useState({});
  // Per-tree question counts: key = `${subject}_${board}_${academic_year}`, value = { topicId: count }
  // Used so table and Question Bank agree (counts scoped to subject → board → class)
  const [questionCountsByTree, setQuestionCountsByTree] = useState({});
  const [expandedGroups, setExpandedGroups] = useState(new Set()); // Track expanded parent groups
  const [isSubjectFixed, setIsSubjectFixed] = useState(false); // Track if subject is pre-selected and fixed
  const [isBoardFixed, setIsBoardFixed] = useState(false); // Track if board is pre-selected and fixed
  const [isAcademicYearFixed, setIsAcademicYearFixed] = useState(false); // Track if academic year is pre-selected and fixed
  const [isParentTopicFixed, setIsParentTopicFixed] = useState(false); // Track if parent topic is pre-selected and fixed
  const [filteredTopics, setFilteredTopics] = useState([]); // Topics filtered by subject/board/academic_year
  const [loadingTopics, setLoadingTopics] = useState(false); // Loading state for topics
  const [masterListDialogOpen, setMasterListDialogOpen] = useState(false); // Master List dialog state
  const [masterListSubject, setMasterListSubject] = useState(''); // Subject for master list
  const [masterListAllTopics, setMasterListAllTopics] = useState([]); // All topics for master list
  const [masterListLoading, setMasterListLoading] = useState(false); // Loading state for master list
  const [masterListSearchTerm, setMasterListSearchTerm] = useState(''); // Search term for filtering topics
  const [masterListCreateParentDialog, setMasterListCreateParentDialog] = useState(false); // Dialog for creating parent topic
  const [masterListCreateChildDialog, setMasterListCreateChildDialog] = useState(false); // Dialog for creating child topic
  const [masterListNewParentName, setMasterListNewParentName] = useState(''); // Name for new parent topic
  const [masterListChildTopicEntries, setMasterListChildTopicEntries] = useState([]); // Child topics queued for creation
  const [masterListChildTopicInput, setMasterListChildTopicInput] = useState('');
  const [masterListEditTopic, setMasterListEditTopic] = useState(null);
  const [masterListEditTopicName, setMasterListEditTopicName] = useState('');
  const [masterListCreating, setMasterListCreating] = useState(false); // Creating state

  // Aggregate counts shown in Master List header
  const masterListParentCount = useMemo(() => {
    if (!masterListAllTopics || masterListAllTopics.length === 0) return 0;
    return masterListAllTopics.filter(isParentTopic).length;
  }, [masterListAllTopics]);

  const masterListChildCount = useMemo(() => {
    if (!masterListAllTopics || masterListAllTopics.length === 0) return 0;
    return masterListAllTopics.filter(isChildTopic).length;
  }, [masterListAllTopics]);

  const masterListCurriculumUsageByTopicId = useMemo(() => {
    const usage = {};
    (associations || []).forEach((assoc) => {
      if (assoc.is_active === false) return;
      const usageKey = `${assoc.subject || 'subject'}_${assoc.board || 'board'}_${assoc.academic_year || 'class'}`;
      [assoc.topic, assoc.parent_mapping_topic].forEach((topicId) => {
        if (topicId == null) return;
        const key = String(topicId);
        if (!usage[key]) usage[key] = new Set();
        usage[key].add(usageKey);
      });
    });
    return Object.fromEntries(
      Object.entries(usage).map(([topicId, curriculumKeys]) => [topicId, curriculumKeys.size])
    );
  }, [associations]);

  const idOf = useCallback((value) => (
    value != null && typeof value === 'object' && 'id' in value ? value.id : value
  ), []);

  const getTreeTopicCount = useCallback((assoc) => {
    if (!assoc) return 0;
    const key = `${idOf(assoc.subject)}_${idOf(assoc.board)}_${idOf(assoc.academic_year)}`;
    const topicId = idOf(assoc.topic);
    return questionCountsByTree[key]?.[topicId] ?? questionCounts[topicId] ?? 0;
  }, [idOf, questionCounts, questionCountsByTree]);

  const curriculumQuestionScope = useMemo(() => {
    const topicsById = new Map((rows || []).map((topic) => [String(topic.id), topic]));
    const activeAssociations = (associations || []).filter((assoc) => assoc.is_active !== false);

    const matchesText = (value, search) => (
      !search || String(value || '').toLowerCase().includes(String(search).toLowerCase())
    );

    const matchesVisibleFilters = (assoc) => {
      const topicId = idOf(assoc.topic);
      const topic = topicsById.get(String(topicId));
      const parentTopicId = assoc.parent_mapping_topic;
      const parentTopic = parentTopicId != null ? topicsById.get(String(parentTopicId)) : null;

      if (filters.board && String(idOf(assoc.board)) !== String(filters.board)) return false;
      if (filters.academic_year && String(idOf(assoc.academic_year)) !== String(filters.academic_year)) return false;
      if (filters.subject && String(idOf(assoc.subject)) !== String(filters.subject)) return false;
      if (filters.topic && !matchesText(topic?.topic || assoc.topic_name, filters.topic)) return false;
      if (filters.parenttopic) {
        const parentName = assoc.parent_mapping_topic_name || parentTopic?.topic || (
          assoc.parent_mapping ? '' : topic?.topic || assoc.topic_name
        );
        if (!matchesText(parentName, filters.parenttopic)) return false;
      }
      return true;
    };

    const scopedAssociations = activeAssociations.filter(matchesVisibleFilters);
    const activeChildAssociationsByParentMapping = new Map();
    activeAssociations.forEach((assoc) => {
      if (!assoc.parent_mapping) return;
      const key = String(assoc.parent_mapping);
      if (!activeChildAssociationsByParentMapping.has(key)) {
        activeChildAssociationsByParentMapping.set(key, []);
      }
      activeChildAssociationsByParentMapping.get(key).push(assoc);
    });

    const hasQuestionTopicIds = new Set();
    const noQuestionTopicIds = new Set();
    const leafTopicIds = new Set();

    scopedAssociations.forEach((assoc) => {
      const topicId = idOf(assoc.topic);
      if (topicId == null) return;
      const topicKey = String(topicId);
      const childAssocs = activeChildAssociationsByParentMapping.get(String(assoc.id)) || [];

      if (assoc.parent_mapping) {
        leafTopicIds.add(topicKey);
        const count = getTreeTopicCount(assoc);
        if (count > 0) {
          hasQuestionTopicIds.add(topicKey);
        } else {
          noQuestionTopicIds.add(topicKey);
        }
        if (assoc.parent_mapping_topic != null) {
          const parentKey = String(assoc.parent_mapping_topic);
          if (count > 0) {
            hasQuestionTopicIds.add(parentKey);
          } else {
            noQuestionTopicIds.add(parentKey);
          }
        }
        return;
      }

      if (childAssocs.length > 0) {
        return;
      }

      leafTopicIds.add(topicKey);
      const count = getTreeTopicCount(assoc);
      if (count > 0) {
        hasQuestionTopicIds.add(topicKey);
      } else {
        noQuestionTopicIds.add(topicKey);
      }
    });

    return {
      hasQuestionTopicIds,
      noQuestionTopicIds,
      leafTopicIds,
    };
  }, [associations, filters.academic_year, filters.board, filters.parenttopic, filters.subject, filters.topic, getTreeTopicCount, idOf, rows]);

  // Helper function to build unique hierarchical keys for tree groups
  // This ensures each group has a unique key that includes all ancestor IDs
  // Parent keys are prefixes of child keys, enabling cascade collapse
  const buildGroupKey = useCallback(({
    level,
    subjectId,
    boardId,
    academicYearId,
    parentTopicId,
  }) => {
    const parts = [
      level || 'none',
      subjectId ?? 'none',
      boardId ?? 'none',
      academicYearId ?? 'none',
      parentTopicId ?? 'none',
    ];
    return parts.join('|');
  }, []);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const getSubjectName = (subjectId) => {
    const subject = subjects.find(s => s.id === subjectId);
    return subject ? subject.name : 'Unknown Subject';
  };

  const fetchQuestionCounts = async (topics) => {
    // No longer needed - question counts are now included in the API response
    // This function is kept for backward compatibility but does nothing
    const counts = {};
    for (const topic of topics) {
      counts[topic.id] = topic.question_count || 0;
    }
    setQuestionCounts(counts);
  };

  // Fetch data
  const fetchData = async () => {
    try {
      setLoading(true);
      // Fetch all topics by using a large page_size
      const { data } = await api.get(`${SUBJECT_TOPICS}?page_size=10000`);
      
      // Handle paginated response
      let topics = data.results || data;
      
      // If there are more pages, fetch them all
      if (data.next) {
        let nextUrl = data.next;
        const allTopics = [...topics];
        
        while (nextUrl) {
          try {
            const nextResponse = await api.get(nextUrl);
            const nextTopics = nextResponse.data.results || [];
            allTopics.push(...nextTopics);
            nextUrl = nextResponse.data.next;
          } catch (e) {
            console.error('Error fetching additional pages:', e);
            break; // Stop fetching if there's an error
          }
        }
        
        topics = allTopics;
      }
      
      setRows(topics);
      
      // Extract question counts from the API response (no separate API calls needed)
      const counts = {};
      for (const topic of topics) {
        counts[topic.id] = topic.question_count || 0;
      }
      setQuestionCounts(counts);
      
      // Fetch subjects, boards, academic years, and associations
      const [subjectsRes, boardsRes, academicYearsRes, associationsRes] = await Promise.all([
        api.get(SUBJECTS),
        api.get(BOARDS + '?published_only=true'),
        api.get(ACADEMIC_YEARS + '?published_only=true'),
        api.get(`${ACADEMIC_YEAR_BOARD_SUBJECT_TOPICS}?page_size=10000`)
      ]);
      setSubjects(subjectsRes.data.results || subjectsRes.data || []);
      setBoards(filterPublished(boardsRes.data.results || boardsRes.data || []));
      setAcademicYears(filterPublished(academicYearsRes.data.results || academicYearsRes.data || []));
      
      // Handle paginated associations
      let allAssociations = associationsRes.data.results || associationsRes.data || [];
      if (associationsRes.data.next) {
        let nextUrl = associationsRes.data.next;
        while (nextUrl) {
          try {
            const nextResponse = await api.get(nextUrl);
            const nextAssociations = nextResponse.data.results || [];
            allAssociations = allAssociations.concat(nextAssociations);
            nextUrl = nextResponse.data.next;
          } catch (e) {
            console.error('Error fetching additional association pages:', e);
            break;
          }
        }
      }
      setAssociations(allAssociations);
    } catch (e) {
      console.error('Error fetching subject topics:', e);
      showSnackbar('Failed to load records', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubjects = async () => {
    try {
      const res = await api.get(SUBJECTS);
      const list = Array.isArray(res.data) ? res.data : (res.data.results || []);
      setSubjects(list);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    fetchData();
    // fetchSubjects() is no longer needed as fetchData already loads subjects
  }, []);

  // Fetch question counts per (subject, board, academic_year) so table matches Question Bank
  useEffect(() => {
    if (!associations || associations.length === 0) {
      setQuestionCountsByTree({});
      return;
    }
    const treeKeys = new Set();
    associations.forEach(a => {
      treeKeys.add(`${a.subject}_${a.board}_${a.academic_year}`);
    });
    let cancelled = false;
    const fetchTreeCounts = async () => {
      const next = {};
      for (const key of treeKeys) {
        if (cancelled) return;
        const [subject, board, academic_year] = key.split('_');
        try {
          const params = new URLSearchParams({ subject, board, academic_year, page_size: 10000 });
          const res = await api.get(`${SUBJECT_TOPICS}?${params}`);
          const raw = res.data.results || res.data || [];
          const list = Array.isArray(raw) ? raw : [];
          next[key] = {};
          list.forEach(t => { next[key][t.id] = t.question_count || 0; });
        } catch (e) {
          console.error('Error fetching question counts for tree', key, e);
          next[key] = {};
        }
      }
      if (!cancelled) setQuestionCountsByTree(prev => ({ ...prev, ...next }));
    };
    fetchTreeCounts();
    return () => { cancelled = true; };
  }, [associations]);

  const handleOpen = useCallback((row, prefillValues = null) => {
    if (row) {
      // If it's a header row (subject, board, academic year, or parent topic), treat it as a new entry with pre-filled values
      if (row.is_subject_header || row.is_board_header || row.is_academic_year_header || row.is_parent_header) {
        setEditingId(null);
        const prefill = prefillValues || {};
        let subjectId = prefill.subject || row.subject;
        
        // For subject headers, use the subject from the row
        if (row.is_subject_header && !subjectId) {
          subjectId = row.subject;
        }
        
        // For board headers, get board from the row
        let boardIds = prefill.boards || [];
        if (row.is_board_header && boardIds.length === 0 && row.board) {
          boardIds = [row.board];
        }
        
        // For board headers, get single board ID
        let boardId = prefill.board || '';
        if (row.is_board_header && !boardId && row.board) {
          boardId = Array.isArray(row.board) ? row.board[0] : row.board;
        }
        
        // Initialize academic year ID
        let academicYearId = prefill.academic_year || '';
        
        // For academic year headers, get board and academic year from the row
        if (row.is_academic_year_header) {
          // Extract board ID from the academic year header row
          if (!boardId && row.board) {
            boardId = Array.isArray(row.board) ? row.board[0] : row.board;
          }
          // Extract academic year ID
          if (!academicYearId && row.academic_year) {
            academicYearId = Array.isArray(row.academic_year) ? row.academic_year[0] : row.academic_year;
          }
        }
        
        // For parent topic headers, extract board and academic year from the row
        if (row.is_parent_header) {
          if (!boardId && row.board) {
            boardId = Array.isArray(row.board) ? row.board[0] : row.board;
          }
          if (!academicYearId && row.academic_year) {
            academicYearId = Array.isArray(row.academic_year) ? row.academic_year[0] : row.academic_year;
          }
        }
        
        // Determine dialog level and set fixed values
        if (row.is_subject_header) {
          setDialogLevel('subject');
          setFixedValues({ subject: subjectId });
          setIsSubjectFixed(true);
          setIsBoardFixed(false);
          setIsAcademicYearFixed(false);
          setIsParentTopicFixed(false);
        } else if (row.is_board_header) {
          setDialogLevel('board');
          setFixedValues({ 
            subject: subjectId,
            boards: boardId ? [boardId] : []
          });
          setIsSubjectFixed(true);
          setIsBoardFixed(false); // Board is not fixed when opened from board level
          setIsAcademicYearFixed(false);
          setIsParentTopicFixed(false);
        } else if (row.is_academic_year_header) {
          setDialogLevel('academic_year');
          setFixedValues({ 
            subject: subjectId,
            boards: boardId ? [boardId] : [],
            academic_years: academicYearId ? [academicYearId] : []
          });
          setIsSubjectFixed(true);
          setIsBoardFixed(true); // Fix board when opened from academic year level
          setIsAcademicYearFixed(true); // Fix academic year when opened from academic year level
          setIsParentTopicFixed(false);
        } else if (row.is_parent_header) {
          setDialogLevel('parent_topic');
          setFixedValues({ 
            subject: subjectId,
            boards: boardId ? [boardId] : [],
            academic_years: academicYearId ? [academicYearId] : []
          });
          setIsSubjectFixed(true);
          setIsBoardFixed(true); // Fix board when opened from parent topic level
          setIsAcademicYearFixed(true); // Fix academic year when opened from parent topic level
          setIsParentTopicFixed(true);
        }
        
        // Initialize form with new structure
        // Selected topics will be loaded by the useEffect that watches board/academic_year
        // Always start with empty selections to avoid showing topics from previous selections
        setForm({ 
          subject: subjectId ? String(subjectId) : '', 
          board: boardId ? String(boardId) : '',
          academic_year: academicYearId ? String(academicYearId) : '',
          curriculumChapters: [createCurriculumChapter()],
        });
        setShowNewSubjectField(false);
        setNewSubjectName('');
        setIsFormDisabled(false);
      } else {
        // Regular edit flow
        // Use originalId if available (for child rows with composite IDs), otherwise use id
        const editTopicId = row.originalId || row.id;
        setEditingId(editTopicId);
      // Check if we have the subject ID or need to find it by name
      let subjectId = row.subject;
      if (!subjectId && row.subject_name) {
        // Try to find the subject ID by name
        const foundSubject = subjects.find(s => s.name === row.subject_name);
        subjectId = foundSubject ? foundSubject.id : '';
        if (!subjectId) {
          console.warn(`Subject "${row.subject_name}" not found in subjects list`);
          showSnackbar(`Warning: Subject "${row.subject_name}" not found in current subjects list`, 'warning');
          // Don't open the dialog if we can't find the subject
          return;
        }
      }
        // For regular edit, get parent topic name instead of ID
        let parentTopicName = '';
        if (row.parenttopic) {
          const parentTopicId = typeof row.parenttopic === 'object' ? row.parenttopic.id : row.parenttopic;
          const parentTopic = rows.find(r => r.id === parentTopicId);
          parentTopicName = parentTopic ? parentTopic.topic : '';
        }
        
        // Regular edit - no level restrictions
        setDialogLevel(null);
        setFixedValues({});
        
        // For editing, we'll use the old form structure temporarily
        // TODO: Update edit flow to use new shuttle structure if needed
        const existingAssociations = associations.filter(assoc => assoc.topic === editTopicId);
        const existingBoardIds = [...new Set(existingAssociations.map(assoc => assoc.board))];
        const existingAcademicYearIds = [...new Set(existingAssociations.map(assoc => assoc.academic_year))];
        
        // Use first board and academic_year for new structure, or empty if none
        setForm({ 
          subject: subjectId, 
          board: existingBoardIds.length > 0 ? String(existingBoardIds[0]) : '',
          academic_year: existingAcademicYearIds.length > 0 ? String(existingAcademicYearIds[0]) : '',
          curriculumChapters: [createCurriculumChapter()],
        });
      setShowNewSubjectField(false);
      setNewSubjectName('');
      setIsFormDisabled(false);
        setIsSubjectFixed(false);
        setIsBoardFixed(false);
        setIsAcademicYearFixed(false);
        setIsParentTopicFixed(false);
      }
    } else {
      // New entry from button - no restrictions
      setEditingId(null);
      setDialogLevel(null);
      setFixedValues({});
      setForm(createEmptyForm());
      setShowNewSubjectField(false);
      setNewSubjectName('');
      setIsFormDisabled(false);
      setIsSubjectFixed(false);
      setIsBoardFixed(false);
      setIsAcademicYearFixed(false);
      setIsParentTopicFixed(false);
    }
    setDialogOpen(true);
  }, [questionCounts, subjects, showSnackbar, rows]);

  const handleClose = useCallback(() => {
    setDialogOpen(false);
    setCurriculumDialogExpanded(false);
    setForm(createEmptyForm());
    setEditingId(null);
    setDialogLevel(null);
    setFixedValues({});
    setShowNewSubjectField(false);
    setNewSubjectName('');
    setIsFormDisabled(false);
    setIsSubjectFixed(false);
    setIsBoardFixed(false);
    setIsAcademicYearFixed(false);
    setIsParentTopicFixed(false);
    setFilteredTopics([]);
  }, []);

  // Fetch all topics for the subject when subject is selected
  // Board and academic_year are used for creating associations, not for filtering topics
  useEffect(() => {
    const fetchFilteredTopics = async () => {
      if (!form.subject) {
        setFilteredTopics([]);
        return;
      }

      setLoadingTopics(true);
      try {
        // Fetch ALL topics for the subject (regardless of board/academic_year associations)
        const topicParams = new URLSearchParams();
        topicParams.append('subject', form.subject);
        topicParams.append('page_size', '10000');
        
        const topicResponse = await api.get(`${SUBJECT_TOPICS}?${topicParams.toString()}`);
        const allTopics = Array.isArray(topicResponse.data) 
          ? topicResponse.data 
          : (topicResponse.data.results || []);
        
        setFilteredTopics(allTopics);
      } catch (error) {
        console.error('Error fetching topics:', error);
        setFilteredTopics([]);
      } finally {
        setLoadingTopics(false);
      }
    };

    fetchFilteredTopics();
  }, [form.subject]);

  // Fetch topics for Master List dialog
  const fetchMasterListTopics = useCallback(async (subjectId) => {
    if (!subjectId || subjectId === 'new') {
      setMasterListAllTopics([]);
      return;
    }

    setMasterListLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('subject', subjectId);
      params.append('page_size', '10000');
      
      const response = await api.get(`${SUBJECT_TOPICS}?${params.toString()}`);
      const topics = Array.isArray(response.data) 
        ? response.data 
        : (response.data.results || []);
      
      setMasterListAllTopics(topics);
      
    } catch (error) {
      console.error('Error fetching master list topics:', error);
      setMasterListAllTopics([]);
    } finally {
      setMasterListLoading(false);
    }
  }, []);

  // When Master List dialog opens, refetch topics for current subject so list is up to date
  useEffect(() => {
    if (masterListDialogOpen && masterListSubject && masterListSubject !== 'new') {
      fetchMasterListTopics(masterListSubject);
    }
  }, [masterListDialogOpen, masterListSubject, fetchMasterListTopics]);

  // Master List now treats parent and child topics as independent vocabulary.
  // Board/class-specific relationships are handled only by curriculum mappings.
  const getMasterListFlatTopics = useMemo(() => {
    if (!masterListAllTopics || masterListAllTopics.length === 0) return { parents: [], children: [] };

    const searchLower = masterListSearchTerm.trim().toLowerCase();
    const matchesSearch = (topic) => !searchLower || (topic.topic || '').toLowerCase().includes(searchLower);
    const sortByName = (a, b) => (a.topic || '').localeCompare(b.topic || '');

    return {
      parents: masterListAllTopics.filter(topic => isParentTopic(topic) && matchesSearch(topic)).sort(sortByName),
      children: masterListAllTopics.filter(topic => isChildTopic(topic) && matchesSearch(topic)).sort(sortByName),
    };
  }, [masterListAllTopics, masterListSearchTerm]);

  const masterListChildTopicOptions = useMemo(() => {
    if (!masterListAllTopics || masterListAllTopics.length === 0) return [];
    return masterListAllTopics;
  }, [masterListAllTopics]);

  // Handle delete topic
  const handleMasterListDeleteTopic = useCallback(async (topicId) => {
    try {
      const curriculumUsageCount = masterListCurriculumUsageByTopicId[String(topicId)] || 0;
      if (curriculumUsageCount > 0) {
        showSnackbar(
          `Cannot delete topic: It is used in ${curriculumUsageCount} curriculum mapping${curriculumUsageCount > 1 ? 's' : ''}`,
          'error'
        );
        return;
      }

      // Check if topic has questions
      const questionCount = questionCounts[topicId] || 0;
      if (questionCount > 0) {
        showSnackbar(`Cannot delete topic: It has ${questionCount} associated question(s)`, 'error');
        return;
      }
      
      // Delete the topic
      await subjectTopicService.remove(topicId);
      showSnackbar('Topic deleted successfully', 'success');
      
      // Refresh topics
      await fetchMasterListTopics(masterListSubject);
      fetchData();
    } catch (error) {
      console.error('Error deleting topic:', error);
      const detail = error.response?.data?.detail;
      const msg = (Array.isArray(detail) ? detail[0] : detail)
        || (Array.isArray(error.response?.data?.non_field_errors) ? error.response.data.non_field_errors[0] : error.response?.data?.non_field_errors)
        || (typeof error.response?.data === 'string' ? error.response.data : null)
        || error.message
        || 'Error deleting topic';
      showSnackbar(msg, 'error');
    }
  }, [masterListSubject, questionCounts, masterListCurriculumUsageByTopicId, fetchMasterListTopics]);

  const handleMasterListChangeTopicType = useCallback(async (topic, nextType) => {
    if (!topic?.id) return;

    const questionCount = questionCounts[topic.id] || 0;
    if (topic.topic_type === 'child' && nextType === 'parent' && questionCount > 0) {
      showSnackbar('Cannot make child topic parent while it has associated questions', 'error');
      return;
    }

    setMasterListLoading(true);
    try {
      await subjectTopicService.update(topic.id, {
        topic_type: nextType,
        parenttopic: null,
      });
      showSnackbar(
        `"${topic.topic}" moved to ${nextType === 'parent' ? 'Parent topics' : 'Child topics'}`,
        'success'
      );
      await fetchMasterListTopics(masterListSubject);
      fetchData();
    } catch (error) {
      console.error('Error changing topic type:', error);
      const msg = error.response?.data?.topic_type?.[0]
        || error.response?.data?.detail
        || (Array.isArray(error.response?.data?.non_field_errors) ? error.response.data.non_field_errors[0] : error.response?.data?.non_field_errors)
        || (typeof error.response?.data === 'string' ? error.response.data : null)
        || error.message
        || 'Error changing topic type';
      showSnackbar(msg, 'error');
    } finally {
      setMasterListLoading(false);
    }
  }, [masterListSubject, questionCounts, fetchMasterListTopics]);

  const handleMasterListOpenEditTopic = useCallback((topic) => {
    setMasterListEditTopic(topic);
    setMasterListEditTopicName(topic?.topic || '');
  }, []);

  const handleMasterListRenameTopic = useCallback(async () => {
    const topicName = masterListEditTopicName.trim();
    if (!masterListEditTopic?.id || !topicName) return;

    if (topicName.toLowerCase() === (masterListEditTopic.topic || '').trim().toLowerCase()) {
      setMasterListEditTopic(null);
      setMasterListEditTopicName('');
      return;
    }

    setMasterListCreating(true);
    try {
      await subjectTopicService.update(masterListEditTopic.id, { topic: topicName });
      showSnackbar('Topic renamed successfully', 'success');
      setMasterListEditTopic(null);
      setMasterListEditTopicName('');
      await fetchMasterListTopics(masterListSubject);
      fetchData();
    } catch (error) {
      console.error('Error renaming topic:', error);
      const msg = error.response?.data?.topic?.[0]
        || error.response?.data?.detail
        || (Array.isArray(error.response?.data?.non_field_errors) ? error.response.data.non_field_errors[0] : error.response?.data?.non_field_errors)
        || (typeof error.response?.data === 'string' ? error.response.data : null)
        || error.message
        || 'Error renaming topic';
      showSnackbar(msg, 'error');
    } finally {
      setMasterListCreating(false);
    }
  }, [masterListEditTopic, masterListEditTopicName, masterListSubject, fetchMasterListTopics]);

  // Handle create parent topic
  const handleMasterListCreateParent = useCallback(async () => {
    if (!masterListNewParentName.trim()) return;
    
    setMasterListCreating(true);
    try {
      const payload = {
        subject: parseInt(masterListSubject, 10),
        topic: masterListNewParentName.trim(),
        topic_type: 'parent',
        parenttopic: null,
      };
      await api.post(SUBJECT_TOPICS, payload);
      
      showSnackbar('Parent topic created successfully', 'success');
      setMasterListNewParentName('');
      setMasterListCreateParentDialog(false);
      await fetchMasterListTopics(masterListSubject);
      fetchData();
    } catch (error) {
      console.error('Error creating parent topic:', error);
      const msg = error.response?.data?.non_field_errors?.[0]
        || error.response?.data?.topic?.[0]
        || error.response?.data?.detail
        || (typeof error.response?.data === 'string' ? error.response.data : null)
        || error.message
        || 'Error creating parent topic';
      showSnackbar(msg, 'error');
      setMasterListNewParentName(''); // Clear so user can enter a different name
    } finally {
      setMasterListCreating(false);
    }
  }, [masterListSubject, masterListNewParentName, fetchMasterListTopics]);

  const normalizeMasterListChildEntry = useCallback((item) => {
    if (typeof item === 'string') {
      const name = item.trim();
      return name ? { id: null, topic: name, _isNew: true } : null;
    }
    if (!item || typeof item !== 'object') return null;
    const name = (item.topic || item.name || '').trim();
    if (!name) return null;
    if (item._isNew) {
      return { id: null, topic: name, _isNew: true };
    }
    return { ...item, topic: name, _isNew: false };
  }, []);

  const updateMasterListChildEntries = useCallback((items) => {
    const seenNames = new Set();
    const nextEntries = [];
    (items || []).forEach((item) => {
      const entry = normalizeMasterListChildEntry(item);
      if (!entry) return;
      const key = entry.topic.toLowerCase();
      if (seenNames.has(key)) return;
      seenNames.add(key);
      nextEntries.push(entry);
    });
    setMasterListChildTopicEntries(nextEntries);
  }, [normalizeMasterListChildEntry]);

  // Handle create child topic
  const handleMasterListCreateChild = useCallback(async () => {
    const topicsToCreate = masterListChildTopicEntries
      .filter((entry) => entry._isNew)
      .map((entry) => entry.topic.trim())
      .filter(Boolean);

    if (topicsToCreate.length === 0) {
      showSnackbar('Enter at least one new child topic name', 'warning');
      return;
    }
    
    setMasterListCreating(true);
    try {
      for (const topicName of topicsToCreate) {
        await api.post(SUBJECT_TOPICS, {
          subject: parseInt(masterListSubject, 10),
          topic: topicName,
          topic_type: 'child',
          parenttopic: null,
        });
      }
      
      showSnackbar(
        `${topicsToCreate.length} child topic${topicsToCreate.length !== 1 ? 's' : ''} created successfully`,
        'success'
      );
      setMasterListChildTopicEntries([]);
      setMasterListChildTopicInput('');
      setMasterListCreateChildDialog(false);
      await fetchMasterListTopics(masterListSubject);
      fetchData();
    } catch (error) {
      console.error('Error creating child topic:', error);
      const msg = error.response?.data?.non_field_errors?.[0]
        || error.response?.data?.topic?.[0]
        || error.response?.data?.detail
        || (typeof error.response?.data === 'string' ? error.response.data : null)
        || error.message
        || 'Error creating child topic';
      showSnackbar(msg, 'error');
    } finally {
      setMasterListCreating(false);
    }
  }, [masterListSubject, masterListChildTopicEntries, fetchMasterListTopics, showSnackbar]);

  // Load existing associations when board and academic_year are selected
  // This pre-populates the selected topics based on existing mappings
  useEffect(() => {
    const loadExistingAssociations = async () => {
      // Only load if we have subject, board, and academic_year
      if (!form.subject || !form.board || !form.academic_year || form.subject === 'new') {
        // Clear selections if board or academic_year is cleared
        if (!form.board || !form.academic_year) {
          setForm(prev => ({ ...prev, curriculumChapters: [createCurriculumChapter()] }));
        }
        return;
      }

      // Wait for topics to be loaded first
      if (filteredTopics.length === 0) {
        return;
      }

      try {
        // Fetch associations ONLY for this specific board, academic_year, and subject combination
        // This ensures we don't show topics from other boards/academic years
        const associationParams = new URLSearchParams();
        associationParams.append('academic_year', String(form.academic_year));
        associationParams.append('board', String(form.board));
        associationParams.append('subject', String(form.subject));
        associationParams.append('page_size', '10000');
        
        const associations = await fetchAllPaginated(`${ACADEMIC_YEAR_BOARD_SUBJECT_TOPICS}?${associationParams.toString()}`);
        
        // Verify associations match the exact board/academic_year/subject combination
        // Filter out any associations that don't match (defensive check)
        const verifiedAssociations = associations.filter(assoc => 
          String(assoc.academic_year) === String(form.academic_year) &&
          String(assoc.board) === String(form.board) &&
          String(assoc.subject) === String(form.subject)
        );
        
        const topicById = new Map(filteredTopics.map(topic => [String(topic.id), topic]));
        const chapterRows = [];
        const rowByParentTopicId = new Map();
        const ensureChapterRow = (parentTopic, order = 0) => {
          if (!parentTopic) return null;
          const key = String(parentTopic.id);
          if (!rowByParentTopicId.has(key)) {
            const row = {
              ...createCurriculumChapter(),
              parentTopic,
              subTopics: [],
              displayOrder: order || 0,
            };
            rowByParentTopicId.set(key, row);
            chapterRows.push(row);
          } else {
            const row = rowByParentTopicId.get(key);
            row.displayOrder = Math.min(row.displayOrder || 0, order || 0);
          }
          return rowByParentTopicId.get(key);
        };

        [...verifiedAssociations]
          .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0) || (a.id ?? 0) - (b.id ?? 0))
          .forEach((assoc) => {
          const topic = topicById.get(String(assoc.topic));
          if (!topic) return;
          const globalParentId = getTopicParentId(topic);
          const mappedParentTopicId = assoc.parent_mapping_topic;
          if (isParentTopic(topic) && !assoc.parent_mapping) {
            ensureChapterRow(topic, assoc.display_order ?? 0);
            return;
          }

          const parentTopic =
            topicById.get(String(mappedParentTopicId || globalParentId)) ||
            null;
          const row = ensureChapterRow(parentTopic, assoc.parent_mapping_display_order ?? assoc.display_order ?? 0);
          if (String(topic.id) === String(parentTopic?.id)) {
            return;
          }
          if (row && !row.subTopics.some((subTopic) => String(subTopic.id) === String(topic.id))) {
            row.subTopics.push(topic);
          }
        });

        chapterRows.sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
        const curriculumChapters = chapterRows.length > 0 ? chapterRows : [createCurriculumChapter()];
        
        // Only update if the selections have actually changed to avoid infinite loops
        // Also ensure we're updating for the correct board/academic_year combination
        setForm(prev => {
          // Double-check that the form still has the same board/academic_year we queried for
          // This prevents race conditions where board/academic_year changed while the API call was in flight
          if (String(prev.board) !== String(form.board) || String(prev.academic_year) !== String(form.academic_year) || String(prev.subject) !== String(form.subject)) {
            // Form values changed, don't update with stale data
            return prev;
          }
          
          const currentKey = JSON.stringify((prev.curriculumChapters || []).map((chapter) => ({
            parent: chapter.parentTopic?.id || '',
            children: (chapter.subTopics || []).map((topic) => topic.id),
          })));
          const nextKey = JSON.stringify(curriculumChapters.map((chapter) => ({
            parent: chapter.parentTopic?.id || '',
            children: (chapter.subTopics || []).map((topic) => topic.id),
          })));
          
          // Only update if selections changed
          if (currentKey !== nextKey) {
            return {
              ...prev,
              curriculumChapters,
            };
          }
          return prev;
        });
      } catch (error) {
        console.error('Error loading existing associations:', error);
        // Don't clear selections on error, just log it
      }
    };

    loadExistingAssociations();
  }, [form.subject, form.board, form.academic_year, filteredTopics]);

  const handleDelete = useCallback(async (id) => {
    // Check if topic has questions
    const questionCount = questionCounts[id] || 0;
    if (questionCount > 0) {
      showSnackbar(`Cannot delete: Topic has ${questionCount} associated question${questionCount > 1 ? 's' : ''}`, 'warning');
      return;
    }

    if (!window.confirm('Delete this record?')) return;
    try {
      await subjectTopicService.remove(id);
      showSnackbar('Record deleted successfully');
      fetchData();
    } catch (e) {
      showSnackbar('Delete failed', 'error');
    }
  }, [questionCounts, showSnackbar]);

  // Separate parent topics from sub-topics. These selections are intentionally
  // independent for each board/class curriculum mapping.
  const getParentTopics = useMemo(() => {
    if (!filteredTopics || filteredTopics.length === 0) return [];

    return filteredTopics.filter(isParentTopic);
  }, [filteredTopics]);

  const getSubTopics = useMemo(() => {
    if (!filteredTopics || filteredTopics.length === 0) return [];

    return filteredTopics.filter(isChildTopic);
  }, [filteredTopics]);

  const selectedChildTopicIds = useMemo(() => {
    const ids = new Set();
    (form.curriculumChapters || []).forEach((chapter) => {
      (chapter.subTopics || []).forEach((topic) => ids.add(String(topic.id)));
    });
    return ids;
  }, [form.curriculumChapters]);

  const updateChapterSubTopics = useCallback((chapterId, nextSubTopicsOrUpdater) => {
    setForm((prev) => ({
      ...prev,
      curriculumChapters: (prev.curriculumChapters || []).map((item) => {
        if (item.id !== chapterId) return item;

        const currentSubTopics = item.subTopics || [];
        const nextSubTopics = typeof nextSubTopicsOrUpdater === 'function'
          ? nextSubTopicsOrUpdater(currentSubTopics)
          : (nextSubTopicsOrUpdater || []);

        return {
          ...item,
          subTopics: nextSubTopics,
        };
      }),
    }));
  }, []);

  const handleSubmit = async () => {
    // Validate required fields
    if (!form.subject) {
      showSnackbar('Subject is required', 'error');
      return;
    }

    if (!form.board) {
      showSnackbar('Board is required', 'error');
      return;
    }

    if (!form.academic_year) {
      showSnackbar('Class is required', 'error');
      return;
    }

    const validChapterRows = (form.curriculumChapters || []).filter(
      (chapter) => chapter.parentTopic && (chapter.subTopics || []).length > 0
    );
    const hasChapterOnlyRows = (form.curriculumChapters || []).some(
      (chapter) => chapter.parentTopic && (chapter.subTopics || []).length === 0
    );
    if (validChapterRows.length === 0 && !hasChapterOnlyRows) {
      showSnackbar('Please add at least one chapter or topic', 'error');
      return;
    }

    // If creating a new subject, validate the subject name
    if (form.subject === 'new' && !newSubjectName.trim()) {
      showSnackbar('New subject name is required', 'error');
      return;
    }

    // If editing and subject is empty, show error
    if (editingId && !form.subject) {
      showSnackbar('Subject is required', 'error');
      return;
    }

    try {
      let subjectId = form.subject;

      // If creating a new subject
      if (form.subject === 'new' && newSubjectName.trim()) {
        try {
          const res = await api.post(SUBJECTS, { name: newSubjectName.trim() });
          subjectId = res.data.id;
          
          // Add the new subject to the subjects list
          setSubjects(prev => [...prev, res.data]);
          
          showSnackbar(`New subject "${newSubjectName.trim()}" created successfully`, 'success');
        } catch (error) {
          console.error('Error creating new subject:', error);
          showSnackbar('Failed to create new subject', 'error');
          return;
        }
      }

      const curriculumRows = (form.curriculumChapters || []).filter((chapter) => chapter.parentTopic);
      const allSelectedTopics = [
        ...curriculumRows.map((chapter) => chapter.parentTopic),
        ...curriculumRows.flatMap((chapter) => chapter.subTopics || []),
      ];

      const selectedTopicIds = new Set(allSelectedTopics.map(t => String(t.id)));

      // Get existing associations for this board/academic_year/subject combination
      const existingAssociationsParams = new URLSearchParams();
      existingAssociationsParams.append('academic_year', String(form.academic_year));
      existingAssociationsParams.append('board', String(form.board));
      existingAssociationsParams.append('subject', String(subjectId));
      existingAssociationsParams.append('page_size', '10000');
      
      let existingAssociations = await fetchAllPaginated(`${ACADEMIC_YEAR_BOARD_SUBJECT_TOPICS}?${existingAssociationsParams.toString()}`);

      // Defensive: only consider associations for THIS board and academic_year
      // (backend should filter, but ensures we never delete another board's associations)
      const formBoardId = parseInt(form.board, 10);
      const formAcademicYearId = parseInt(form.academic_year, 10);
      const formSubjectId = parseInt(subjectId, 10);
      existingAssociations = existingAssociations.filter(assoc =>
        Number(assoc.board) === formBoardId &&
        Number(assoc.academic_year) === formAcademicYearId &&
        Number(assoc.subject) === formSubjectId
      );

      // Identify associations to delete (exist in database for this board/year but not in current selection)
      const associationsToDelete = existingAssociations.filter(assoc => 
        assoc.is_active !== false && !selectedTopicIds.has(String(assoc.topic))
      );

      // Disallow removal only for topics that have questions in this tree (subject → board → class)
      // Identify associations to create (in current selection but not in database)
      const existingByTopicId = new Map(existingAssociations.map(assoc => [String(assoc.topic), assoc]));

      // Process deletions and creations
      const associationPromises = [];
      let createdCount = 0;
      let updatedCount = 0;
      let deletedCount = 0;
      let errorCount = 0;

      // Delete associations that are no longer selected. Remove child rows first so
      // parent chapter rows with children do not briefly leave orphan mappings.
      const associationsToDeleteOrdered = [...associationsToDelete].sort((a, b) => {
        const aOrder = a.parent_mapping ? 0 : 1;
        const bOrder = b.parent_mapping ? 0 : 1;
        return aOrder - bOrder;
      });

      for (const assoc of associationsToDeleteOrdered) {
        try {
          associationPromises.push(
            api.delete(`${ACADEMIC_YEAR_BOARD_SUBJECT_TOPICS}${assoc.id}/`)
              .then(() => {
                deletedCount++;
              })
              .catch(err => {
                console.error(`Error deleting association ${assoc.id}:`, err);
                errorCount++;
              })
          );
        } catch (err) {
          console.error(`Error processing deletion for association ${assoc.id}:`, err);
          errorCount++;
        }
      }

      const ensureAssociation = async (topic, parentMappingId = null, displayOrder = 0) => {
        const existing = existingByTopicId.get(String(topic.id));
        if (existing) {
          const currentParentMapping = existing.parent_mapping || null;
          const currentDisplayOrder = existing.display_order ?? 0;
          if (
            String(currentParentMapping || '') !== String(parentMappingId || '') ||
            Number(currentDisplayOrder) !== Number(displayOrder) ||
            existing.is_active === false
          ) {
            await api.patch(`${ACADEMIC_YEAR_BOARD_SUBJECT_TOPICS}${existing.id}/`, {
              parent_mapping: parentMappingId,
              display_order: displayOrder,
              is_active: true,
            });
            existing.parent_mapping = parentMappingId;
            existing.display_order = displayOrder;
            existing.is_active = true;
            updatedCount++;
          }
          return existing;
        }
        const response = await api.post(ACADEMIC_YEAR_BOARD_SUBJECT_TOPICS, {
          academic_year: parseInt(form.academic_year, 10),
          board: parseInt(form.board, 10),
          subject: parseInt(subjectId, 10),
          topic: topic.id,
          parent_mapping: parentMappingId,
          display_order: displayOrder,
          is_active: true,
        });
        createdCount++;
        existingByTopicId.set(String(topic.id), response.data);
        return response.data;
      };

      for (const [chapterIndex, chapter] of curriculumRows.entries()) {
        try {
          const parentAssoc = await ensureAssociation(chapter.parentTopic, null, chapterIndex);
          for (const [childIndex, childTopic] of (chapter.subTopics || []).entries()) {
            await ensureAssociation(childTopic, parentAssoc.id, childIndex);
          }
        } catch (err) {
          console.error(`Error processing curriculum chapter ${chapter.parentTopic?.id}:`, err);
          errorCount++;
        }
      }

      // Wait for all operations to complete
      await Promise.all(associationPromises);

      // Show appropriate success message
      const changes = [];
      if (deletedCount > 0) changes.push(`deleted ${deletedCount} association${deletedCount !== 1 ? 's' : ''}`);
      if (createdCount > 0) changes.push(`created ${createdCount} association${createdCount !== 1 ? 's' : ''}`);
      if (updatedCount > 0) changes.push(`updated ${updatedCount} relationship${updatedCount !== 1 ? 's' : ''}`);
      
      if (errorCount > 0) {
        showSnackbar(`${changes.join(' and ')}, but ${errorCount} operation${errorCount !== 1 ? 's' : ''} failed`, 'warning');
      } else if (changes.length > 0) {
        showSnackbar(`Successfully ${changes.join(' and ')}`, 'success');
      } else {
        showSnackbar('No changes to save', 'info');
      }

      // Refresh data and close dialog
      handleClose();
      fetchData();
    } catch (e) {
      console.error('Error in handleSubmit:', e);
      const errorMessage = e.response?.data?.detail || e.message || 'Operation failed';
      showSnackbar(errorMessage, 'error');
    }
  };

  // Apply filters manually since filterData utility seems to have issues
  // Filter rows first
  const baseFilteredRows = useMemo(() => {
    // If board or academic_year filters are set, we need to filter by associations
    const hasBoardFilter = filters.board && filters.board !== '';
    const hasAcademicYearFilter = filters.academic_year && filters.academic_year !== '';
    
    // If filtering by board or academic year, we need to check associations
    if (hasBoardFilter || hasAcademicYearFilter) {
      const filteredByAssociation = rows.filter(row => {
        // Find associations for this topic
        const topicAssocs = associations.filter(assoc => assoc.topic === row.id);
        
        if (topicAssocs.length === 0) {
          // If no associations, exclude if filters are set
          return !hasBoardFilter && !hasAcademicYearFilter;
        }
        
        // Normalize ID (API may return number or object with id)
        const idOf = (v) => (v != null && typeof v === 'object' && 'id' in v ? v.id : v);
        const matchesBoard = !hasBoardFilter || topicAssocs.some(assoc =>
          String(idOf(assoc.board)) === String(filters.board)
        );
        const matchesAcademicYear = !hasAcademicYearFilter || topicAssocs.some(assoc =>
          String(idOf(assoc.academic_year)) === String(filters.academic_year)
        );
        
        return matchesBoard && matchesAcademicYear;
      });
      
      // Apply other filters to the association-filtered rows
      return filteredByAssociation.filter(row => {
        // Subject filter
        if (filters.subject && filters.subject !== '') {
          if (row.subject !== parseInt(filters.subject)) {
            return false;
          }
        }

        // Topic filter
        if (filters.topic && filters.topic !== '') {
          const topicName = (row.topic || '').toLowerCase();
          if (!topicName.includes(filters.topic.toLowerCase())) {
            return false;
          }
        }

        // Parent topic filter
        if (filters.parenttopic && filters.parenttopic !== '') {
          const parentTopicName = (row.parenttopic_name || '').toLowerCase();
          if (!parentTopicName.includes(filters.parenttopic.toLowerCase())) {
            return false;
          }
        }

        // Question count filter
        if (filters.questionCount && filters.questionCount !== '') {
          const topicId = String(row.id);
          
          if (filters.questionCount === 'has_questions') {
            return curriculumQuestionScope.hasQuestionTopicIds.has(topicId);
          } else if (filters.questionCount === 'no_questions') {
            return curriculumQuestionScope.noQuestionTopicIds.has(topicId);
          }
        }

        return true;
      });
    }
    
    // No board/academic year filters, apply regular filters
    return rows.filter(row => {
      // Subject filter
      if (filters.subject && filters.subject !== '') {
        if (row.subject !== parseInt(filters.subject)) {
          return false;
        }
      }

      // Topic filter
      if (filters.topic && filters.topic !== '') {
        const topicName = (row.topic || '').toLowerCase();
        if (!topicName.includes(filters.topic.toLowerCase())) {
          return false;
        }
      }

      // Parent topic filter
      if (filters.parenttopic && filters.parenttopic !== '') {
        const parentTopicName = (row.parenttopic_name || '').toLowerCase();
        if (!parentTopicName.includes(filters.parenttopic.toLowerCase())) {
          return false;
        }
      }

      // Question count filter
      if (filters.questionCount && filters.questionCount !== '') {
        const topicId = String(row.id);
        
        if (filters.questionCount === 'has_questions') {
          return curriculumQuestionScope.hasQuestionTopicIds.has(topicId);
        } else if (filters.questionCount === 'no_questions') {
          return curriculumQuestionScope.noQuestionTopicIds.has(topicId);
        }
      }

      return true;
    });
  }, [rows, filters, associations, curriculumQuestionScope]);

  const topicStatisticRows = useMemo(() => (
    baseFilteredRows.filter((row) => curriculumQuestionScope.leafTopicIds.has(String(row.id)))
  ), [baseFilteredRows, curriculumQuestionScope]);

  // Total question count per tree (subject + board + academic year) for dialog note
  const treeQuestionCount = useMemo(() => {
    const map = {};
    Object.keys(questionCountsByTree).forEach(key => {
      const byTopic = questionCountsByTree[key];
      map[key] = Object.values(byTopic || {}).reduce((s, n) => s + (n || 0), 0);
    });
    return map;
  }, [questionCountsByTree]);

  // Expand all subjects, boards, academic years, and parent topics by default when data is loaded
  // Only run once when data is first loaded (expandedGroups.size === 0)
  useEffect(() => {
    if (baseFilteredRows.length > 0 && expandedGroups.size === 0 && associations.length > 0 && buildGroupKey) {
      const defaultExpanded = new Set();
      
      // Get unique subjects
      const subjectIds = [...new Set(baseFilteredRows.map(r => {
        const subjectId = r.subject 
          ? (typeof r.subject === 'object' ? r.subject.id : r.subject)
          : null;
        return subjectId;
      }).filter(Boolean))];
      
      subjectIds.forEach(subjectId => {
        // Expand subject
        const subjectKey = buildGroupKey({
          level: 'subject',
          subjectId: String(subjectId),
          boardId: 'none',
          academicYearId: 'none',
          parentTopicId: 'none',
        });
        defaultExpanded.add(subjectKey);
        
        // Get all boards for this subject from associations
        const subjectAssocs = associations.filter(a => {
          const assocSubjectId = a.subject 
            ? (typeof a.subject === 'object' ? a.subject.id : a.subject)
            : null;
          return String(assocSubjectId) === String(subjectId);
        });
        
        const boardIds = [...new Set(subjectAssocs.map(a => a.board).filter(Boolean))];
        boardIds.forEach(boardId => {
          // Expand board
          const boardKey = buildGroupKey({
            level: 'board',
            subjectId: String(subjectId),
            boardId: String(boardId),
            academicYearId: 'none',
            parentTopicId: 'none',
          });
          defaultExpanded.add(boardKey);
          
          // Get all academic years for this board
          const boardAssocs = subjectAssocs.filter(a => String(a.board) === String(boardId));
          const academicYearIds = [...new Set(boardAssocs.map(a => a.academic_year).filter(Boolean))];
          
          academicYearIds.forEach(academicYearId => {
            // Expand academic year
            const yearKey = buildGroupKey({
              level: 'academicYear',
              subjectId: String(subjectId),
              boardId: String(boardId),
              academicYearId: String(academicYearId),
              parentTopicId: 'none',
            });
            defaultExpanded.add(yearKey);
            
            // Get all parent topics for this academic year
            const yearTopics = baseFilteredRows.filter(r => {
              const rSubjectId = r.subject 
                ? (typeof r.subject === 'object' ? r.subject.id : r.subject)
                : null;
              const topicAssocs = associations.filter(a => 
                String(a.topic) === String(r.id) &&
                String(a.subject) === String(subjectId) &&
                String(a.board) === String(boardId) &&
                String(a.academic_year) === String(academicYearId)
              );
              return String(rSubjectId) === String(subjectId) && topicAssocs.length > 0;
            });
            
            const parentIds = new Set();
            yearTopics.forEach(row => {
              const parentId = row.parenttopic 
                ? (typeof row.parenttopic === 'object' ? row.parenttopic.id : row.parenttopic)
                : null;
              if (parentId) {
                parentIds.add(parentId);
              }
            });
            
            // Expand all parent topics
            parentIds.forEach(parentId => {
              const parentKey = buildGroupKey({
                level: 'parentTopic',
                subjectId: String(subjectId),
                boardId: String(boardId),
                academicYearId: String(academicYearId),
                parentTopicId: String(parentId),
              });
              defaultExpanded.add(parentKey);
            });
            
            // Expand "No Parent" if there are orphan topics
            const hasOrphans = yearTopics.some(r => {
              const parentId = r.parenttopic 
                ? (typeof r.parenttopic === 'object' ? r.parenttopic.id : r.parenttopic)
                : null;
              return !parentId;
            });
            
            if (hasOrphans) {
              const noParentKey = buildGroupKey({
                level: 'noParent',
                subjectId: String(subjectId),
                boardId: String(boardId),
                academicYearId: String(academicYearId),
                parentTopicId: 'none',
              });
              defaultExpanded.add(noParentKey);
            }
          });
        });
      });
      
      if (defaultExpanded.size > 0) {
        setExpandedGroups(defaultExpanded);
      }
    }
  }, [baseFilteredRows, expandedGroups, associations, buildGroupKey]);

  // Transform filtered rows into tree structure: Subject -> Board -> Academic Year -> Parent Topic -> Topic
  const treeRows = useMemo(() => {
    if (!baseFilteredRows.length) {
      return [];
    }
    
    // If no associations, build tree with "No Association" for board and academic year
    // This ensures the tree structure is consistent even when associations don't exist

    // Build a map of topic -> [associations] for quick lookup
    const topicAssociationsMap = new Map(); // topicId -> [{ board, academic_year, ... }]
    associations.forEach(assoc => {
      const topicId = assoc.topic;
      if (!topicAssociationsMap.has(topicId)) {
        topicAssociationsMap.set(topicId, []);
      }
      topicAssociationsMap.get(topicId).push(assoc);
    });

    // Group by Subject -> Board -> Academic Year -> Parent Topic -> Topic
    const subjectGroups = new Map(); // subjectId -> { subject: name, boards: Map }
    
    baseFilteredRows.forEach(row => {
      const subjectId = row.subject 
        ? (typeof row.subject === 'object' ? row.subject.id : row.subject)
        : null;
      const subjectName = row.subject_name || 'Unknown Subject';
      const topicId = row.id;
      
      // Get associations for this topic
      const topicAssocs = topicAssociationsMap.get(topicId) || [];
      
      // Prefer the board/class-specific chapter mapping when present.
      const globalParentId = row.parenttopic 
        ? (typeof row.parenttopic === 'object' ? row.parenttopic.id : row.parenttopic)
        : null;
      
      // Skip topics without associations - only show topics with board associations
      if (topicAssocs.length === 0) {
        return; // Skip topics without associations
      }
      
      // If no associations, create a "No Association" entry (this code should not be reached now)
      if (false) {
        if (!subjectGroups.has(subjectId)) {
          subjectGroups.set(subjectId, {
            subject: subjectName,
            subjectId: subjectId,
            boards: new Map()
          });
        }
        const subjectGroup = subjectGroups.get(subjectId);
        if (!subjectGroup.boards.has('NO_ASSOC')) {
          subjectGroup.boards.set('NO_ASSOC', {
            boardId: 'NO_ASSOC',
            boardName: 'No Association',
            academicYears: new Map()
          });
        }
        const boardGroup = subjectGroup.boards.get('NO_ASSOC');
        if (!boardGroup.academicYears.has('NO_ASSOC')) {
          boardGroup.academicYears.set('NO_ASSOC', {
            academicYearId: 'NO_ASSOC',
            academicYearName: 'No Association',
            parentTopics: new Map(),
            orphanTopics: []
          });
        }
        const yearGroup = boardGroup.academicYears.get('NO_ASSOC');
        const parentId = row.parenttopic 
          ? (typeof row.parenttopic === 'object' ? row.parenttopic.id : row.parenttopic)
          : null;
        if (parentId) {
          if (!yearGroup.parentTopics.has(parentId)) {
            const parentTopic = baseFilteredRows.find(r => r.id === parentId);
            if (parentTopic) {
              yearGroup.parentTopics.set(parentId, {
                parent: parentTopic,
                children: [],
                displayOrder: 0
              });
            }
          }
          if (yearGroup.parentTopics.has(parentId)) {
            // Check if this child is not already added
            if (!yearGroup.parentTopics.get(parentId).children.find(c => c.id === row.id)) {
              yearGroup.parentTopics.get(parentId).children.push(row);
            }
          }
        } else {
          // Check if this orphan is not already added
          if (!yearGroup.orphanTopics.find(t => t.id === row.id)) {
            yearGroup.orphanTopics.push(row);
          }
        }
      } else {
        // When board/academic_year filters are set, only process associations that match
        const idOf = (v) => (v != null && typeof v === 'object' && 'id' in v ? v.id : v);
        const topicAssocsFiltered = (filters.board || filters.academic_year)
          ? topicAssocs.filter(assoc =>
              (!filters.board || String(idOf(assoc.board)) === String(filters.board)) &&
              (!filters.academic_year || String(idOf(assoc.academic_year)) === String(filters.academic_year))
            )
          : topicAssocs;
        if (topicAssocsFiltered.length === 0) return;
        topicAssocsFiltered.forEach(assoc => {
          const boardId = idOf(assoc.board);
          const boardName = assoc.board_name || 'Unknown Board';
          const academicYearId = idOf(assoc.academic_year);
          const academicYearName = assoc.academic_year_display || 'Unknown Year';
          const standaloneParent = isParentTopic(row) && !assoc.parent_mapping;
          const parentId = standaloneParent ? topicId : (assoc.parent_mapping_topic || globalParentId);

          if (!parentId) {
            return;
          }
          
          if (!subjectGroups.has(subjectId)) {
            subjectGroups.set(subjectId, {
              subject: subjectName,
              subjectId: subjectId,
              boards: new Map()
            });
          }
          const subjectGroup = subjectGroups.get(subjectId);
          
          if (!subjectGroup.boards.has(boardId)) {
            subjectGroup.boards.set(boardId, {
              boardId: boardId,
              boardName: boardName,
              academicYears: new Map()
            });
          }
          const boardGroup = subjectGroup.boards.get(boardId);
          
          if (!boardGroup.academicYears.has(academicYearId)) {
            boardGroup.academicYears.set(academicYearId, {
              academicYearId: academicYearId,
              academicYearName: academicYearName,
              parentTopics: new Map(),
              orphanTopics: []
            });
          }
          const yearGroup = boardGroup.academicYears.get(academicYearId);
          
          // parentId is already checked above, so it's guaranteed to exist here
          if (parentId) {
            if (!yearGroup.parentTopics.has(parentId)) {
              const parentTopic = String(parentId) === String(row.id)
                ? row
                : baseFilteredRows.find(r => String(r.id) === String(parentId));
              if (parentTopic) {
                yearGroup.parentTopics.set(parentId, {
                  parent: parentTopic,
                  children: [],
                  displayOrder: assoc.parent_mapping_display_order ?? assoc.display_order ?? 0
                });
              }
            }
            if (yearGroup.parentTopics.has(parentId)) {
              const parentGroup = yearGroup.parentTopics.get(parentId);
              parentGroup.displayOrder = Math.min(
                parentGroup.displayOrder ?? Number.MAX_SAFE_INTEGER,
                assoc.parent_mapping_display_order ?? assoc.display_order ?? 0
              );
              if (String(row.id) === String(parentId)) {
                return;
              }
              // Check if this child is not already added
              if (!parentGroup.children.find(c => c.id === row.id)) {
                parentGroup.children.push({
                  ...row,
                  display_order: assoc.display_order ?? 0,
                });
              }
            }
          }
          // Removed orphan topics handling - we only want sub-topics with parent topics
        });
      }
    });

    // Build tree structure
    const treeStructure = [];
    const addedRowIds = new Set(); // Track added row IDs to prevent duplicates
    const sortedSubjects = Array.from(subjectGroups.entries()).sort((a, b) => {
      return a[1].subject.localeCompare(b[1].subject);
    });
    
    sortedSubjects.forEach(([subjectId, subjectGroup]) => {
      // Filter out boards with "NO_ASSOC" and academic years without parent topics
      const validBoards = Array.from(subjectGroup.boards.entries()).filter(([boardId, boardGroup]) => {
        if (boardId === 'NO_ASSOC') return false;
        // Check if board has at least one academic year with parent topics
        return Array.from(boardGroup.academicYears.values()).some(yearGroup => 
          yearGroup.parentTopics.size > 0
        );
      });
      
      // Skip subjects that don't have any valid boards
      if (validBoards.length === 0) {
        return;
      }
      
      const subjectGroupKey = buildGroupKey({
        level: 'subject',
        subjectId: String(subjectId),
        boardId: 'none',
        academicYearId: 'none',
        parentTopicId: 'none',
      });
      const isSubjectExpanded = expandedGroups.has(subjectGroupKey);
      
      // Add subject header (only if not already added)
      if (!addedRowIds.has(subjectGroupKey)) {
        addedRowIds.add(subjectGroupKey);
        treeStructure.push({
          id: subjectGroupKey,
          subject: subjectId,
          subject_name: subjectGroup.subject,
          board: '',
          board_name: '',
          academic_year: '',
          academic_year_name: '',
          topic: '',
          is_group_header: true,
          is_subject_header: true,
          groupKey: subjectGroupKey,
          group_id: subjectGroupKey, // Keep for backward compatibility
          children_count: validBoards.reduce((sum, [, boardGroup]) => 
            sum + Array.from(boardGroup.academicYears.values()).filter(yearGroup => 
              yearGroup.parentTopics.size > 0 && yearGroup.academicYearId !== 'NO_ASSOC'
            ).length, 0),
          _isSubject: true
        });
      }
      
      if (isSubjectExpanded) {
        // Sort boards (only valid boards, filtered above)
        const sortedBoards = validBoards.sort((a, b) => {
          return a[1].boardName.localeCompare(b[1].boardName);
        });
        
        sortedBoards.forEach(([boardId, boardGroup]) => {
          // Skip "No Association" boards - only show boards with associations
          if (boardId === 'NO_ASSOC') {
            return;
          }
          
          const boardGroupKey = buildGroupKey({
            level: 'board',
            subjectId: String(subjectId),
            boardId: String(boardId),
            academicYearId: 'none',
            parentTopicId: 'none',
          });
          const isBoardExpanded = expandedGroups.has(boardGroupKey);
          
          // Add board header (only if not already added)
          if (!addedRowIds.has(boardGroupKey)) {
            addedRowIds.add(boardGroupKey);
            treeStructure.push({
              id: boardGroupKey,
              subject: subjectId,
              subject_name: '',
              board: boardId,
              board_name: boardGroup.boardName,
              academic_year: '',
              academic_year_name: '',
              topic: '',
              is_group_header: true,
              is_board_header: true,
              groupKey: boardGroupKey,
              group_id: boardGroupKey, // Keep for backward compatibility
              children_count: boardGroup.academicYears.size,
              _isBoard: true
            });
          }
          
          if (isBoardExpanded) {
            // Sort academic years
            const sortedYears = Array.from(boardGroup.academicYears.entries()).sort((a, b) => {
              return a[1].academicYearName.localeCompare(b[1].academicYearName);
            });
            
            sortedYears.forEach(([academicYearId, yearGroup]) => {
              const yearGroupKey = buildGroupKey({
                level: 'academicYear',
                subjectId: String(subjectId),
                boardId: String(boardId),
                academicYearId: String(academicYearId),
                parentTopicId: 'none',
              });
              const isYearExpanded = expandedGroups.has(yearGroupKey);
              
              // Add academic year header (only if not already added)
              if (!addedRowIds.has(yearGroupKey)) {
                addedRowIds.add(yearGroupKey);
                treeStructure.push({
                  id: yearGroupKey,
                  subject: subjectId,
                  subject_name: '',
                  board: boardId,
                  board_name: '',
                  academic_year: academicYearId,
                  academic_year_name: yearGroup.academicYearName,
                  topic: '',
                  is_group_header: true,
                  is_academic_year_header: true,
                  groupKey: yearGroupKey,
                  group_id: yearGroupKey, // Keep for backward compatibility
                  children_count: yearGroup.parentTopics.size,
                  _isAcademicYear: true
                });
              }
              
              if (isYearExpanded) {
                // Add parent topic groups
                const sortedParentGroups = Array.from(yearGroup.parentTopics.entries()).sort((a, b) => {
                  return (a[1].displayOrder ?? 0) - (b[1].displayOrder ?? 0)
                    || (a[1].parent.topic || '').localeCompare(b[1].parent.topic || '');
                });
                
                sortedParentGroups.forEach(([parentId, parentGroup]) => {
                  const parentGroupKey = buildGroupKey({
                    level: 'parentTopic',
                    subjectId: String(subjectId),
                    boardId: String(boardId),
                    academicYearId: String(academicYearId),
                    parentTopicId: String(parentId),
                  });
                  const isParentExpanded = expandedGroups.has(parentGroupKey);
                  
                  // Add parent topic header (only if not already added)
                  if (!addedRowIds.has(parentGroupKey)) {
                    addedRowIds.add(parentGroupKey);
                    treeStructure.push({
                      ...parentGroup.parent,
                      id: parentGroupKey, // Ensure parent topic has the groupKey as its ID
                      originalId: parentGroup.parent.id, // Topic ID for question count
                      subject_name: '',
                      board: boardId,
                      board_name: '',
                      academic_year: academicYearId,
                      academic_year_name: '',
                      is_group_header: true,
                      is_parent_header: true,
                      groupKey: parentGroupKey,
                      group_id: parentGroupKey, // Keep for backward compatibility
                      children_count: parentGroup.children.length,
                      childTopicIds: parentGroup.children.map(child => child.id),
                      _isParent: true
                    });
                  }
                  
                  if (isParentExpanded) {
                    const sortedChildren = [...parentGroup.children].sort((a, b) => {
                      return (a.display_order ?? 0) - (b.display_order ?? 0)
                        || (a.topic || '').localeCompare(b.topic || '');
                    });
                    // Use a Set to track added topic IDs to prevent duplicates
                    const addedTopicIds = new Set();
                    sortedChildren.forEach(child => {
                      const childRowId = `topic_${child.id}_${boardId}_${academicYearId}`; // Unique ID including board and academic year
                      const uniqueKey = `${child.id}_${boardId}_${academicYearId}`;
                      if (!addedTopicIds.has(uniqueKey) && !addedRowIds.has(childRowId)) {
                        addedTopicIds.add(uniqueKey);
                        addedRowIds.add(childRowId);
                        treeStructure.push({
                          ...child,
                          id: childRowId, // Unique ID including board and academic year
                          originalId: child.id, // Store original topic ID for lookups
                          subject_name: '',
                          board: boardId,
                          board_name: '',
                          academic_year: academicYearId,
                          academic_year_name: '',
                          is_group_header: false,
                          groupKey: parentGroupKey, // Child topics inherit parent's groupKey
                          group_id: parentGroupKey, // Keep for backward compatibility
                          _isChild: true
                        });
                      }
                    });
                  }
                });
                
                // Removed "No Parent" group - only showing sub-topics with parent topics
              }
            });
          }
        });
      }
    });

    return treeStructure;
  }, [baseFilteredRows, expandedGroups, associations, buildGroupKey, filters]);

  const toggleGroup = useCallback((groupKey) => {
    if (!groupKey || typeof groupKey !== 'string') return;
    
    setExpandedGroups(prev => {
      const next = new Set(prev);
      
      const isExpanded = next.has(groupKey);
      
      if (isExpanded) {
        // Collapsing: remove this key *and* all children that start with it
        // Since parent keys are prefixes of child keys (separated by '|'),
        // we can use prefix matching to find all descendants
        next.delete(groupKey);
        
        // Remove all child groups that start with this group's key followed by '|'
        // This handles cascade collapse: when collapsing an academic year,
        // all parent topics under it are also collapsed
        const prefixToMatch = groupKey + '|';
        for (const key of Array.from(next)) {
          if (key.startsWith(prefixToMatch)) {
            next.delete(key);
          }
        }
      } else {
        // Expanding: just add this group
        next.add(groupKey);
      }
      
      return next;
    });
  }, []);



  const handleFilterChange = useCallback((field) => (e) => {
    const value = e?.target?.value ?? e ?? '';
    setFilters(prev => {
      const next = { ...prev, [field]: value };
      // When Parent Topic changes, clear Topic so the dropdown shows only children of the new parent
      if (field === 'parenttopic') next.topic = '';
      return next;
    });
  }, []);
  const handleClearFilters = useCallback(() => clearFilters(setFilters, { board: '', academic_year: '', subject: '', topic: '', parenttopic: '', questionCount: '' }), []);

  // Memoize filter config: Parent Topic before Topic; use select dropdowns so options list is visible
  const filterConfig = useMemo(() => {
    // Parent topic names: from parenttopic_name (parents of children) and topic names of rows with no parent (root parents)
    const fromChildren = rows.map(row => row.parenttopic_name).filter(Boolean);
    const fromRoots = rows.filter(r => !r.parenttopic || r.parenttopic === null).map(r => r.topic).filter(Boolean);
    const uniqueParentTopics = [...new Set([...fromChildren, ...fromRoots])].sort();
    const parentTopicSelected = (filters.parenttopic || '').trim();
    // When a parent topic is selected, Topic dropdown shows only child topics of that parent
    const uniqueTopics = parentTopicSelected
      ? [...new Set(rows.filter(r => (r.parenttopic_name || '').trim() === parentTopicSelected).map(r => r.topic).filter(Boolean))].sort()
      : [...new Set(rows.map(row => row.topic).filter(Boolean))].sort();

    return [
      {
        field: 'board',
        label: 'Board',
        type: 'select',
        options: [
          ...boards.map(board => ({
            value: board.id.toString(),
            label: board.name
          }))
        ]
      },
      {
        field: 'academic_year',
        label: 'Academic Year',
        type: 'select',
        options: [
          ...academicYears.map(year => ({
            value: year.id.toString(),
            label: year.year
          }))
        ]
      },
      {
        field: 'subject',
        label: 'Subject',
        type: 'select',
        options: [
          ...subjects.map(subject => ({
            value: subject.id.toString(),
            label: subject.name
          }))
        ]
      },
      {
        field: 'parenttopic',
        label: 'Parent Topic',
        type: 'select',
        options: uniqueParentTopics.map(name => ({ value: name, label: name }))
      },
      {
        field: 'topic',
        label: 'Topic',
        type: 'select',
        options: uniqueTopics.map(name => ({ value: name, label: name }))
      },
      {
        field: 'questionCount',
        label: 'Question Status',
        type: 'select',
        options: [
          { value: 'has_questions', label: 'Has Questions' },
          { value: 'no_questions', label: 'No Questions' }
        ]
      }
    ];
  }, [subjects, rows, boards, academicYears, filters.parenttopic]);

  // Memoize columns to prevent unnecessary re-renders
  const columns = useMemo(() => [
    {
      field: 'subject_name',
      headerName: 'Subject',
      flex: 1.5,
      renderCell: (params) => {
        const isSubjectHeader = params.row.is_subject_header;
        const isBoardHeader = params.row.is_board_header;
        const isAcademicYearHeader = params.row.is_academic_year_header;
        const isParentHeader = params.row.is_parent_header;
        const isChild = params.row._isChild;
        const groupKey = params.row.groupKey || params.row.group_id; // Use groupKey, fallback to group_id for backward compatibility
        const isExpanded = expandedGroups.has(groupKey);
        
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isSubjectHeader && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  if (groupKey) {
                    toggleGroup(groupKey);
                  }
                }}
                sx={{ p: 0.5 }}
              >
                {isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
              </IconButton>
            )}
            {(isBoardHeader || isAcademicYearHeader || isParentHeader) && (
              <Box sx={{ width: 24 }} />
            )}
            {isChild && (
              <Box sx={{ width: 24 }} />
            )}
            {isSubjectHeader ? (
              <Chip
                label={params.value || '—'}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ fontWeight: 'bold' }}
              />
            ) : params.value ? (
        <Chip
          label={params.value}
          size="small"
          color="primary"
          variant="outlined"
        />
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                —
              </Typography>
            )}
          </Box>
        );
      }
    },
    {
      field: 'board_name',
      headerName: 'Board',
      flex: 1.5,
      renderCell: (params) => {
        const isSubjectHeader = params.row.is_subject_header;
        const isBoardHeader = params.row.is_board_header;
        const isAcademicYearHeader = params.row.is_academic_year_header;
        const isParentHeader = params.row.is_parent_header;
        const isChild = params.row._isChild;
        const groupKey = params.row.groupKey || params.row.group_id; // Use groupKey, fallback to group_id for backward compatibility
        const isExpanded = expandedGroups.has(groupKey);
        
        if (isSubjectHeader) {
          return (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              —
            </Typography>
          );
        }
        
        return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isBoardHeader && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  if (groupKey) {
                    toggleGroup(groupKey);
                  }
                }}
                sx={{ p: 0.5 }}
              >
                {isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
              </IconButton>
            )}
            {(isAcademicYearHeader || isParentHeader) && (
              <Box sx={{ width: 24 }} />
            )}
            {isChild && (
              <Box sx={{ width: 24 }} />
            )}
            {isBoardHeader ? (
              <Chip
                label={params.value || '—'}
                size="small"
                color="secondary"
                variant="outlined"
                sx={{ fontWeight: 'bold' }}
              />
            ) : params.value ? (
              <Chip
                label={params.value}
                size="small"
                color="secondary"
                variant="outlined"
              />
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                —
          </Typography>
            )}
        </Box>
        );
      }
    },
    {
      field: 'academic_year_name',
      headerName: 'Academic Year',
      flex: 1.5,
      renderCell: (params) => {
        const isSubjectHeader = params.row.is_subject_header;
        const isBoardHeader = params.row.is_board_header;
        const isAcademicYearHeader = params.row.is_academic_year_header;
        const isParentHeader = params.row.is_parent_header;
        const isChild = params.row._isChild;
        const groupKey = params.row.groupKey || params.row.group_id; // Use groupKey, fallback to group_id for backward compatibility
        const isExpanded = expandedGroups.has(groupKey);
        
        if (isSubjectHeader || isBoardHeader) {
          return (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              —
            </Typography>
          );
        }
        
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isAcademicYearHeader && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  if (groupKey) {
                    toggleGroup(groupKey);
                  }
                }}
                sx={{ p: 0.5 }}
              >
                {isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
              </IconButton>
            )}
            {isParentHeader && (
              <Box sx={{ width: 24 }} />
            )}
            {isChild && (
              <Box sx={{ width: 24 }} />
            )}
            {isAcademicYearHeader ? (
          <Chip
                label={params.value || '—'}
                size="small"
                color="info"
                variant="outlined"
                sx={{ fontWeight: 'bold' }}
              />
            ) : params.value ? (
              <Chip
                label={params.value}
            size="small"
            color="info"
            variant="outlined"
          />
        ) : (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                —
          </Typography>
            )}
          </Box>
        );
      }
    },
    {
      field: 'topic',
      headerName: 'Parent Topic / Topic',
      flex: 2,
      renderCell: (params) => {
        const isSubjectHeader = params.row.is_subject_header;
        const isBoardHeader = params.row.is_board_header;
        const isAcademicYearHeader = params.row.is_academic_year_header;
        const isParentHeader = params.row.is_parent_header;
        const isChild = params.row._isChild;
        const groupKey = params.row.groupKey || params.row.group_id; // Use groupKey, fallback to group_id for backward compatibility
        const isExpanded = expandedGroups.has(groupKey);
        
        // Don't show anything for subject, board, or academic year headers
        if (isSubjectHeader || isBoardHeader || isAcademicYearHeader) {
          return (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              —
            </Typography>
          );
        }
        
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: isChild ? 5 : (isParentHeader ? 2.5 : 0) }}>
            {isParentHeader && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  // Use the groupKey directly - it's already unique and hierarchical
                  if (groupKey && typeof groupKey === 'string') {
                    toggleGroup(groupKey);
                  }
                }}
                sx={{ p: 0.5, ml: -0.5 }}
              >
                {isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
              </IconButton>
            )}
            {isChild && (
              <Box sx={{ width: 28, display: 'flex', alignItems: 'center' }}>
                <Box sx={{ width: 1, height: 16, bgcolor: 'divider', mr: 1.5 }} />
              </Box>
            )}
            <Typography 
              variant="body2" 
              sx={{ 
                fontWeight: isParentHeader ? 'bold' : 'normal',
                color: isParentHeader ? 'primary.main' : 'text.primary'
              }}
            >
              {params.value}
              {(isParentHeader && params.row.children_count > 0) && (
                <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                  ({params.row.children_count} {params.row.children_count === 1 ? 'child' : 'children'})
                </Typography>
              )}
            </Typography>
          </Box>
        );
      }
    },
    {
      field: 'questionCount',
      headerName: 'Question Count',
      description: 'Number of questions for this topic in the selected curriculum context.',
      flex: 1,
      renderCell: (params) => {
        // Show — for subject, board, and academic year headers (no topic)
        if (params.row.is_subject_header || params.row.is_board_header || params.row.is_academic_year_header) {
          return (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              —
            </Typography>
          );
        }
        // Topic level: show count for this tree (subject -> board -> class) so it matches Question Bank.
        // Parent rows roll up only their displayed child topics, not the parent topic's own questions.
        const topicId = params.row.originalId != null ? params.row.originalId : params.row.id;
        const treeKey = params.row.subject != null && params.row.board != null && params.row.academic_year != null
          ? `${params.row.subject}_${params.row.board}_${params.row.academic_year}`
          : null;
        const scopedCounts = treeKey ? questionCountsByTree[treeKey] : null;
        const count = params.row.is_parent_header
          ? (params.row.childTopicIds || []).reduce(
              (sum, childTopicId) => sum + (scopedCounts?.[childTopicId] ?? 0),
              0
            )
          : treeKey
            ? (scopedCounts?.[topicId] ?? 0)
            : (questionCounts[topicId] || 0);
        return (
          <Chip
            label={count}
            size="small"
            color={count > 0 ? "secondary" : "default"}
            variant="outlined"
          />
        );
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        // Show Add icon only for academic year headers (class level)
        if (params.row.is_academic_year_header) {
          return (
            <Tooltip title="Add topic to this academic year">
              <IconButton
                size="small"
                onClick={() => handleOpen(params.row)}
                sx={{ p: 0.5, color: 'primary.main' }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          );
        }
        
        // Hide action buttons for subject, board, and parent topic headers
        if (params.row.is_subject_header || params.row.is_board_header || params.row.is_parent_header) {
          return null;
        }

        // Hide action icons for child topics
        if (params.row._isChild) {
          return null;
        }

        // Regular edit/delete actions for non-child topics (should not reach here based on current filtering)
        // Use originalId if available (for child rows with composite IDs), otherwise use id
        const topicId = params.row.originalId || params.row.id;
        const questionCount = questionCounts[topicId] || 0;
        const hasQuestions = questionCount > 0;
        const editTooltip = hasQuestions
          ? 'Edit topic (only parent topic can be changed when questions are assigned)'
          : 'Edit topic';
        const deleteTooltip = hasQuestions
          ? 'Cannot delete: Topic has associated questions'
          : 'Delete topic';

        return (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title={editTooltip}>
              <span>
                <IconButton
                  size="small"
                  onClick={() => handleOpen(params.row)}
                  sx={{ p: 0.5 }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={deleteTooltip}>
              <span>
                <IconButton
                  size="small"
                  onClick={() => handleDelete(topicId)}
                  disabled={hasQuestions}
                  sx={{ p: 0.5, color: 'error.main' }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        );
      }
    }
  ], [questionCounts, questionCountsByTree, expandedGroups, handleOpen, handleDelete, rows]);

  // Memoize action buttons
  const actionButtons = useMemo(() => [
    {
      label: 'Curriculum Builder',
      icon: <AddIcon />,
      variant: 'contained',
      onClick: () => handleOpen(null)
    },
    {
      label: 'Master List',
      icon: <AddIcon />,
      variant: 'outlined',
      onClick: () => {
        setMasterListSearchTerm('');
        setMasterListDialogOpen(true);
      }
    }
  ], []);

  const emptyStateProps = {
    title: 'No subjects and topics found',
    description: 'Try adjusting your filters or add your first subject and topic.',
    icon: <AddIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
  };

  return (
    <AdminLayoutWrapper>
      <DataTableLayout
        title="Manage Curriculum"
        actionButtons={actionButtons}
        filterPanel={
          <FilterPanel
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            filterConfig={filterConfig}
          />
        }
        loading={loading}
        isEmpty={!loading && treeRows.length === 0}
        emptyStateProps={emptyStateProps}
        mobileFilterOpen={mobileFilterOpen}
        onMobileFilterClose={() => setMobileFilterOpen(!mobileFilterOpen)}
      >
        {/* Statistics Display */}
        {!loading && baseFilteredRows.length > 0 && (
          <Box sx={{ mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Topic Statistics
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Chip
                label={`${topicStatisticRows.filter(r => curriculumQuestionScope.hasQuestionTopicIds.has(String(r.id))).length} with questions`}
                size="small"
                color="secondary"
                variant="outlined"
              />
              <Chip
                label={`${topicStatisticRows.filter(r => curriculumQuestionScope.noQuestionTopicIds.has(String(r.id))).length} without questions`}
                size="small"
                color="default"
                variant="outlined"
              />
              <Chip
                label={`${topicStatisticRows.length} total topics`}
                size="small"
                color="primary"
                variant="outlined"
              />
            </Box>

                         {/* Warning message if all question counts are 0 */}
             {topicStatisticRows.length > 0 && topicStatisticRows.every(r => curriculumQuestionScope.noQuestionTopicIds.has(String(r.id))) && (
               <Alert severity="info" sx={{ mt: 2 }}>
                 <Typography variant="body2">
                   <strong>Note:</strong> Question counts are currently showing as 0. This may indicate that no questions have been created yet or questions are not linked to topics.
                 </Typography>
               </Alert>
             )}
             
             
          </Box>
        )}

        <Box sx={{ mt: 2, flex: 1, minHeight: 0, width: '100%', display: 'flex', flexDirection: 'column' }}>
          <DataGrid
            rows={treeRows}
            columns={columns}
            pagination={dataGridProps.pagination}
            pageSizeOptions={dataGridProps.pageSizeOptions}
            rowsPerPageOptions={dataGridProps.rowsPerPageOptions}
            disableRowSelectionOnClick={dataGridProps.disableRowSelectionOnClick}
            autoHeight={false}
            getRowId={(row) => {
              // Ensure every row has a stable, unique ID
              // Never use Math.random() as it creates new IDs on every render
              if (row.id) {
                return String(row.id);
              }
              // For rows without an ID, create a stable composite key
              // This should never happen if tree structure is built correctly
              const compositeKey = [
                row.groupKey || 'unknown',
                row.subject || 'none',
                row.board || 'none',
                row.academic_year || 'none',
                row.topic || 'none'
              ].join('|');
              return compositeKey;
            }}
            getRowClassName={(params) => {
              if (params.row.is_subject_header) {
                return 'subject-header-row';
              }
              if (params.row.is_parent_header) {
                return 'parent-topic-row';
              }
              if (params.row._isChild) {
                return 'child-topic-row';
              }
              return '';
            }}
            sx={{
              flex: 1,
              minHeight: 0,
              '& .MuiDataGrid-root': {
                height: '100%'
              },
              '& .subject-header-row': {
                backgroundColor: 'rgba(25, 118, 210, 0.15)',
                fontWeight: 'bold',
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.2)',
                },
              },
              '& .board-header-row': {
                backgroundColor: 'rgba(156, 39, 176, 0.12)',
                fontWeight: 'bold',
                '&:hover': {
                  backgroundColor: 'rgba(156, 39, 176, 0.16)',
                },
              },
              '& .academic-year-header-row': {
                backgroundColor: 'rgba(0, 188, 212, 0.12)',
                fontWeight: 'bold',
                '&:hover': {
                  backgroundColor: 'rgba(0, 188, 212, 0.16)',
                },
              },
              '& .parent-topic-row': {
                backgroundColor: 'rgba(25, 118, 210, 0.08)',
                fontWeight: 'bold',
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.12)',
                },
              },
              '& .child-topic-row': {
                backgroundColor: 'rgba(0, 0, 0, 0.02)',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              },
            }}
            components={{
              Toolbar: () => (
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                  p: 1
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <GridToolbarColumnsButton
                      sx={{
                        color: 'primary.main',
                        '&:hover': { backgroundColor: 'primary.50' }
                      }}
                    />
                    <GridToolbarDensitySelector
                      sx={{
                        color: 'primary.main',
                        '&:hover': { backgroundColor: 'primary.50' }
                      }}
                    />
                    <GridToolbarExport
                      sx={{
                        color: 'primary.main',
                        '&:hover': { backgroundColor: 'primary.50' }
                      }}
                    />
                  </Box>
                </Box>
              )
            }}
          />
        </Box>
      </DataTableLayout>

      {/* Add/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={(event, reason) => {
          if (reason === 'backdropClick') return;
          handleClose();
        }}
        maxWidth={curriculumDialogExpanded ? "lg" : "sm"}
        fullWidth
        fullScreen={curriculumDialogExpanded}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
          <Box>
          {editingId ? 'Edit Subject & Topic' : 'Curriculum Builder'}
          {editingId && questionCounts[editingId] > 0 && (
            <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 1 }}>
              ⚠️ This topic has {questionCounts[editingId]} associated question{questionCounts[editingId] > 1 ? 's' : ''}. Only the parent topic can be changed.
            </Typography>
          )}
          </Box>
          <Tooltip title={curriculumDialogExpanded ? 'Restore size' : 'Expand window'}>
            <IconButton
              size="small"
              aria-label={curriculumDialogExpanded ? 'Restore window size' : 'Expand window'}
              onClick={() => setCurriculumDialogExpanded((prev) => !prev)}
            >
              {curriculumDialogExpanded ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
          </Tooltip>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {editingId
              ? 'Edit the subject and topic for this record.'
              : 'Select subject, board, and class, then select or create parent topics and sub-topics.'
            }
          </Typography>
          {form.subject && form.subject !== 'new' && form.board && form.academic_year && (treeQuestionCount[`${form.subject}_${form.board}_${form.academic_year}`] ?? 0) > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Removing a chapter or topic here removes only its curriculum mapping. Existing questions and master topics are not deleted.
            </Typography>
          )}

          {/* Subject Selection */}
          <FormControl fullWidth margin="normal">
            <InputLabel id="subject-label">Subject</InputLabel>
            <Select
              labelId="subject-label"
              value={form.subject || ''}
              label="Subject"
              disabled={isFormDisabled || (editingId && questionCounts[editingId] > 0) || isSubjectFixed}
              onChange={(e) => {
                const value = e.target.value;
                setForm({ 
                  ...form, 
                  subject: value,
                  board: '',
                  academic_year: '',
                  curriculumChapters: [createCurriculumChapter()],
                });
                setShowNewSubjectField(value === 'new');
                if (value !== 'new') {
                  setNewSubjectName('');
                }
              }}
            >
              <MenuItem value="">
                <em>Select a subject</em>
              </MenuItem>
              {subjects.map(subject => (
                <MenuItem key={subject.id} value={subject.id}>
                  {subject.name}
                </MenuItem>
              ))}
              {!isSubjectFixed && (
              <MenuItem value="new" sx={{
                borderTop: '1px solid',
                borderColor: 'divider',
                color: 'primary.main',
                fontWeight: 'medium'
              }}>
                <em>+ Create New Subject</em>
              </MenuItem>
              )}
            </Select>
          </FormControl>

          {showNewSubjectField && (
            <>
              <TextField
                label="New Subject Name"
                value={newSubjectName}
                onChange={(e) => setNewSubjectName(e.target.value)}
                fullWidth
                margin="normal"
                placeholder="Enter new subject name"
                autoFocus
                disabled={isFormDisabled || (editingId && questionCounts[editingId] > 0)}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                A new subject will be created and then linked to this topic.
              </Typography>
            </>
          )}

          {/* Board Selection - Single Select */}
          <FormControl fullWidth margin="normal" disabled={!form.subject || form.subject === 'new' || isBoardFixed}>
            <InputLabel id="board-label">Board</InputLabel>
            <Select
              labelId="board-label"
              value={form.board || ''}
              label="Board"
              onChange={(e) => {
                const newBoard = e.target.value;
                setForm({ 
                  ...form, 
                  board: newBoard,
                  academic_year: '',
                  curriculumChapters: [createCurriculumChapter()],
                });
              }}
            >
              <MenuItem value="">
                <em>Select a board</em>
              </MenuItem>
              {boards.map(board => (
                <MenuItem key={board.id} value={board.id}>
                  {board.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Academic Year (Class) Selection - Single Select */}
          <FormControl fullWidth margin="normal" disabled={!form.board || isAcademicYearFixed}>
            <InputLabel id="academic-year-label">Class</InputLabel>
            <Select
              labelId="academic-year-label"
              value={form.academic_year || ''}
              label="Class"
              onChange={(e) => {
                const newAcademicYear = e.target.value;
                setForm({ 
                  ...form, 
                  academic_year: newAcademicYear,
                  curriculumChapters: [createCurriculumChapter()],
                });
              }}
            >
              <MenuItem value="">
                <em>Select a class</em>
              </MenuItem>
              {academicYears.map(year => (
                <MenuItem key={year.id} value={year.id}>
                  {year.year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Show shuttles when subject is selected (board and academic_year are optional for viewing, required for creating associations) */}
          {form.subject && form.subject !== 'new' && (
            <>
              <Divider sx={{ my: 2 }} />
              
              {loadingTopics ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <Typography>Loading topics...</Typography>
                </Box>
              ) : (
                <>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {(form.curriculumChapters || []).map((chapter, index) => {
                      const selectedParentIds = new Set(
                        (form.curriculumChapters || [])
                          .filter((item) => item.id !== chapter.id && item.parentTopic)
                          .map((item) => String(item.parentTopic.id))
                      );
                      const parentOptions = getParentTopics.filter(
                        (topic) => !selectedParentIds.has(String(topic.id))
                      );
                      const childOptions = getSubTopics.filter((topic) => (
                        !selectedChildTopicIds.has(String(topic.id)) ||
                        (chapter.subTopics || []).some((selected) => String(selected.id) === String(topic.id))
                      ));

                      return (
                        <Paper key={chapter.id} variant="outlined" sx={{ p: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, flexShrink: 0 }}>
                              Chapter {index + 1}
                            </Typography>
                            <Box sx={{ flex: 1 }} />
                            <Tooltip title="Move chapter up">
                              <span>
                                <IconButton
                                  size="small"
                                  aria-label="Move chapter up"
                                  disabled={index === 0}
                                  onClick={() => {
                                    setForm({
                                      ...form,
                                      curriculumChapters: moveArrayItem(form.curriculumChapters || [], index, index - 1),
                                    });
                                  }}
                                >
                                  <KeyboardArrowUpIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Move chapter down">
                              <span>
                                <IconButton
                                  size="small"
                                  aria-label="Move chapter down"
                                  disabled={index === (form.curriculumChapters || []).length - 1}
                                  onClick={() => {
                                    setForm({
                                      ...form,
                                      curriculumChapters: moveArrayItem(form.curriculumChapters || [], index, index + 1),
                                    });
                                  }}
                                >
                                  <KeyboardArrowDownIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <IconButton
                              size="small"
                              aria-label="Remove chapter"
                              disabled={(form.curriculumChapters || []).length === 1}
                              onClick={() => {
                                setForm({
                                  ...form,
                                  curriculumChapters: (form.curriculumChapters || []).filter((item) => item.id !== chapter.id),
                                });
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                          <Autocomplete
                            options={parentOptions}
                            value={chapter.parentTopic || null}
                            onChange={(event, newValue) => {
                              setForm({
                                ...form,
                                curriculumChapters: (form.curriculumChapters || []).map((item) =>
                                  item.id === chapter.id ? { ...item, parentTopic: newValue || null } : item
                                ),
                              });
                            }}
                            getOptionLabel={(option) => option?.topic || option?.name || ''}
                            isOptionEqualToValue={(option, value) => String(option?.id) === String(value?.id)}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Chapter"
                                placeholder="Type to search parent topics..."
                                size="small"
                              />
                            )}
                            noOptionsText="No parent topics found"
                            sx={{ mb: 2 }}
                          />
                          <Autocomplete
                            multiple
                            filterSelectedOptions
                            options={childOptions}
                            value={chapter.subTopics || []}
                            onChange={(event, newValue) => {
                              updateChapterSubTopics(chapter.id, newValue || []);
                            }}
                            getOptionLabel={(option) => option?.topic || option?.name || ''}
                            isOptionEqualToValue={(option, value) => String(option?.id) === String(value?.id)}
                            renderTags={() => null}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Topics"
                                placeholder="Type to add child topics..."
                                size="small"
                              />
                            )}
                            noOptionsText="No child topics found"
                          />
                          {(chapter.subTopics || []).length > 0 && (
                            <Box
                              sx={{
                                mt: 1.5,
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1,
                                overflow: 'hidden',
                                bgcolor: 'background.paper',
                              }}
                            >
                              {(chapter.subTopics || []).map((topic, childIndex) => (
                                <Box
                                  key={`${chapter.id}-topic-${topic.id}`}
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    px: 1.25,
                                    py: 0.75,
                                    borderTop: childIndex === 0 ? 0 : '1px solid',
                                    borderColor: 'divider',
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ width: 28, flexShrink: 0 }}
                                  >
                                    {childIndex + 1}
                                  </Typography>
                                  <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
                                    {topic?.topic || topic?.name || `Topic ${topic?.id || childIndex + 1}`}
                                  </Typography>
                                  <Tooltip title="Move topic up">
                                    <span>
                                      <IconButton
                                        size="small"
                                        aria-label="Move topic up"
                                        disabled={childIndex === 0}
                                        onClick={() => {
                                          updateChapterSubTopics(chapter.id, (subTopics) =>
                                            moveArrayItem(subTopics, childIndex, childIndex - 1)
                                          );
                                        }}
                                      >
                                        <KeyboardArrowUpIcon fontSize="small" />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                  <Tooltip title="Move topic down">
                                    <span>
                                      <IconButton
                                        size="small"
                                        aria-label="Move topic down"
                                        disabled={childIndex === (chapter.subTopics || []).length - 1}
                                        onClick={() => {
                                          updateChapterSubTopics(chapter.id, (subTopics) =>
                                            moveArrayItem(subTopics, childIndex, childIndex + 1)
                                          );
                                        }}
                                      >
                                        <KeyboardArrowDownIcon fontSize="small" />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                  <Tooltip title="Remove topic">
                                    <IconButton
                                      size="small"
                                      aria-label="Remove topic"
                                      onClick={() => {
                                        updateChapterSubTopics(chapter.id, (subTopics) =>
                                          subTopics.filter((subTopic) => String(subTopic.id) !== String(topic.id))
                                        );
                                      }}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              ))}
                            </Box>
                          )}
                        </Paper>
                      );
                    })}

                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => {
                        setForm({
                          ...form,
                          curriculumChapters: [...(form.curriculumChapters || []), createCurriculumChapter()],
                        });
                      }}
                    >
                      Add Chapter
                    </Button>
                  </Box>
                </>
              )}
            </>
          )}


          {editingId ? (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              {questionCounts[editingId] > 0 
                ? 'Note: This topic has associated questions. Only the parent topic can be modified to preserve question references.'
                : 'Note: Editing a topic will not affect existing questions. Questions will continue to reference this topic.'
              }
            </Typography>
          ) : (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Note: After creating a topic, you can add questions to it in the Question Bank section.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isFormDisabled}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Master List Dialog */}
      <Dialog open={masterListDialogOpen} onClose={() => setMasterListDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6">Master List - Manage Topics</Typography>
              {masterListAllTopics && masterListAllTopics.length > 0 && (
                <Typography variant="body2" color="text.secondary">
                  {masterListParentCount} parents, {masterListChildCount} child topics
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => {
                  setMasterListNewParentName('');
                  setMasterListCreateParentDialog(true);
                }}
                disabled={!masterListSubject || masterListSubject === 'new'}
              >
                Add Parent Topic
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => {
                  setMasterListChildTopicEntries([]);
                  setMasterListChildTopicInput('');
                  setMasterListCreateChildDialog(true);
                }}
                disabled={!masterListSubject || masterListSubject === 'new'}
              >
                Add Child Topic
              </Button>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select a subject to manage reusable parent topics and child topics. Curriculum associations are configured per board and class in the curriculum builder.
          </Typography>

          {/* Subject Selection */}
          <FormControl fullWidth margin="normal">
            <InputLabel id="master-list-subject-label">Subject</InputLabel>
            <Select
              labelId="master-list-subject-label"
              value={masterListSubject || ''}
              label="Subject"
              onChange={(e) => {
                const subjectId = e.target.value;
                setMasterListSubject(subjectId);
                setMasterListSearchTerm('');
                // Fetch topics for this subject
                if (subjectId && subjectId !== 'new') {
                  fetchMasterListTopics(subjectId);
                } else {
                  setMasterListAllTopics([]);
                }
              }}
            >
              <MenuItem value="">
                <em>Select a subject</em>
              </MenuItem>
              {subjects.map(subject => (
                <MenuItem key={subject.id} value={subject.id}>
                  {subject.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {masterListSubject && masterListSubject !== 'new' && (
            <>
              <Divider sx={{ my: 2 }} />

              {/* Search Bar */}
              <TextField
                fullWidth
                size="small"
                placeholder="Search topics..."
                value={masterListSearchTerm}
                onChange={(e) => setMasterListSearchTerm(e.target.value)}
                sx={{ mb: 2 }}
              />
              
              {masterListLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <Typography>Loading topics...</Typography>
                </Box>
              ) : (
                <Box sx={{ maxHeight: 500, overflow: 'auto' }}>
                  {getMasterListFlatTopics.parents.length === 0 && getMasterListFlatTopics.children.length === 0 ? (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        {masterListSearchTerm ? 'No topics found matching your search' : 'No topics available. Create a topic to get started.'}
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                      {[
                        { title: 'Parent topics', topics: getMasterListFlatTopics.parents, isParent: true },
                        { title: 'Child topics', topics: getMasterListFlatTopics.children, isParent: false },
                      ].map((section) => (
                        <Paper key={section.title} variant="outlined" sx={{ overflow: 'hidden' }}>
                          <Box sx={{ px: 2, py: 1.25, borderBottom: 1, borderColor: 'divider', bgcolor: 'action.hover' }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                              {section.title}
                              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                ({section.topics.length})
                              </Typography>
                            </Typography>
                          </Box>
                          {section.topics.length === 0 ? (
                            <Box sx={{ p: 2 }}>
                              <Typography variant="body2" color="text.secondary">
                                No {section.isParent ? 'parent' : 'child'} topics found.
                              </Typography>
                            </Box>
                          ) : (
                            <List dense disablePadding>
                              {section.topics.map((topic) => {
                                const topicQuestionCount = questionCounts[topic.id] || 0;
                                const curriculumUsageCount = masterListCurriculumUsageByTopicId[String(topic.id)] || 0;
                                const canDeleteTopic = topicQuestionCount === 0 && curriculumUsageCount === 0;
                                const nextTopicType = section.isParent ? 'child' : 'parent';
                                const canChangeTopicType = section.isParent || topicQuestionCount === 0;
                                const changeTypeTooltip = section.isParent
                                  ? 'Move to Child topics'
                                  : topicQuestionCount > 0
                                    ? 'Cannot make child topic parent: Has associated questions'
                                    : 'Move to Parent topics';
                                const deleteTooltip = curriculumUsageCount > 0
                                  ? `Cannot delete: Used in ${curriculumUsageCount} curriculum mapping${curriculumUsageCount > 1 ? 's' : ''}`
                                  : topicQuestionCount > 0
                                    ? 'Cannot delete: Has associated questions'
                                    : `Delete ${section.isParent ? 'parent' : 'child'} topic`;
                                return (
                                  <ListItem
                                    key={`${section.title}-${topic.id}`}
                                    sx={{
                                      py: 0.75,
                                      borderBottom: '1px solid',
                                      borderColor: 'divider',
                                      '&:last-child': { borderBottom: 0 },
                                    }}
                                  >
                                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1 }}>
                                      <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
                                        {topic.topic}
                                      </Typography>
                                      {topicQuestionCount > 0 && (
                                        <Chip
                                          label={`${topicQuestionCount} question${topicQuestionCount > 1 ? 's' : ''}`}
                                          size="small"
                                          color="warning"
                                        />
                                      )}
                                      <Tooltip title="Edit topic name">
                                        <span>
                                          <IconButton
                                            size="small"
                                            onClick={() => handleMasterListOpenEditTopic(topic)}
                                            disabled={masterListLoading}
                                            color="primary"
                                          >
                                            <EditIcon fontSize="small" />
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                      <Tooltip title={changeTypeTooltip}>
                                        <span>
                                          <IconButton
                                            size="small"
                                            onClick={() => handleMasterListChangeTopicType(topic, nextTopicType)}
                                            disabled={!canChangeTopicType || masterListLoading}
                                            color="primary"
                                          >
                                            <SwapHorizIcon fontSize="small" />
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                      <Tooltip title={deleteTooltip}>
                                        <span>
                                          <IconButton
                                            size="small"
                                            onClick={() => handleMasterListDeleteTopic(topic.id)}
                                            disabled={!canDeleteTopic}
                                            sx={{ color: 'error.main' }}
                                          >
                                            <DeleteIcon fontSize="small" />
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                    </Box>
                                  </ListItem>
                                );
                              })}
                            </List>
                          )}
                        </Paper>
                      ))}
                    </Box>
                  )}
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMasterListDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Topic Name Dialog */}
      <Dialog
        open={Boolean(masterListEditTopic)}
        onClose={() => {
          if (!masterListCreating) {
            setMasterListEditTopic(null);
            setMasterListEditTopicName('');
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Topic Name</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Renaming keeps existing questions and curriculum mappings linked to this topic.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Topic Name"
            value={masterListEditTopicName}
            onChange={(e) => setMasterListEditTopicName(e.target.value)}
            margin="normal"
            disabled={masterListCreating}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && masterListEditTopicName.trim() && !masterListCreating) {
                handleMasterListRenameTopic();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setMasterListEditTopic(null);
              setMasterListEditTopicName('');
            }}
            disabled={masterListCreating}
          >
            Cancel
          </Button>
          <Button
            onClick={handleMasterListRenameTopic}
            variant="contained"
            disabled={!masterListEditTopicName.trim() || masterListCreating}
          >
            {masterListCreating ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Parent Topic Dialog */}
      <Dialog
        open={masterListCreateParentDialog}
        onClose={() => {
          if (!masterListCreating) {
            setMasterListNewParentName('');
            setMasterListCreateParentDialog(false);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create Parent Topic</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Topic Name"
            value={masterListNewParentName}
            onChange={(e) => setMasterListNewParentName(e.target.value)}
            margin="normal"
            disabled={masterListCreating}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && masterListNewParentName.trim() && !masterListCreating) {
                handleMasterListCreateParent();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMasterListCreateParentDialog(false)} disabled={masterListCreating}>
            Cancel
          </Button>
          <Button
            onClick={handleMasterListCreateParent}
            variant="contained"
            disabled={!masterListNewParentName.trim() || masterListCreating}
          >
            {masterListCreating ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Child Topic Dialog */}
      <Dialog
        open={masterListCreateChildDialog}
        onClose={() => {
          if (!masterListCreating) {
            setMasterListChildTopicEntries([]);
            setMasterListChildTopicInput('');
            setMasterListCreateChildDialog(false);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create Child Topics</DialogTitle>
        <DialogContent>
          <Autocomplete
            multiple
            freeSolo
            filterSelectedOptions
            options={masterListChildTopicOptions}
            value={masterListChildTopicEntries}
            inputValue={masterListChildTopicInput}
            onInputChange={(event, newValue) => setMasterListChildTopicInput(newValue)}
            onChange={(event, newValue, reason) => {
              updateMasterListChildEntries(newValue);
              if (['createOption', 'selectOption'].includes(reason)) {
                setMasterListChildTopicInput('');
              }
            }}
            getOptionLabel={(option) => {
              if (typeof option === 'string') return option;
              return option?.topic || option?.name || '';
            }}
            isOptionEqualToValue={(option, value) => {
              if (!option || !value) return false;
              const optionName = (option.topic || option.name || '').toLowerCase();
              const valueName = (value.topic || value.name || '').toLowerCase();
              if (option._isNew || value._isNew || !option.id || !value.id) {
                return optionName === valueName;
              }
              return String(option.id) === String(value.id);
            }}
            filterOptions={(options, params) => {
              const inputValue = params.inputValue.trim();
              const filtered = options.filter((option) =>
                (option.topic || '').toLowerCase().includes(inputValue.toLowerCase())
              );
              const exactExisting = options.some(
                (option) => (option.topic || '').toLowerCase() === inputValue.toLowerCase()
              );
              const alreadySelected = masterListChildTopicEntries.some(
                (entry) => (entry.topic || '').toLowerCase() === inputValue.toLowerCase()
              );
              if (inputValue && !exactExisting && !alreadySelected) {
                filtered.push({ id: null, topic: inputValue, _isNew: true });
              }
              return filtered;
            }}
            disabled={masterListCreating}
            renderInput={(params) => (
              <TextField
                {...params}
                autoFocus
                label="Child Topics"
                margin="normal"
                placeholder="Type a topic and press Enter"
                helperText="Existing child topics appear in search. New duplicate names are not created."
                onKeyDown={(event) => {
                  if (event.key !== 'Enter') return;
                  const topicName = masterListChildTopicInput.trim();
                  if (!topicName) return;
                  event.preventDefault();

                  const existingTopic = masterListChildTopicOptions.find(
                    (topic) => (topic.topic || '').toLowerCase() === topicName.toLowerCase()
                  );
                  updateMasterListChildEntries([
                    ...masterListChildTopicEntries,
                    existingTopic || { id: null, topic: topicName, _isNew: true },
                  ]);
                  setMasterListChildTopicInput('');
                }}
              />
            )}
            renderTags={(value, getTagProps) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                {value.map((topic, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={topic._isNew ? `new-${topic.topic}-${index}` : topic.id}
                    label={topic.topic}
                    size="small"
                    color={topic._isNew ? 'primary' : 'default'}
                    variant={topic._isNew ? 'filled' : 'outlined'}
                  />
                ))}
              </Box>
            )}
            renderOption={(props, option) => {
              if (option._isNew) {
                return (
                  <Box component="li" {...props} key={`new-${option.topic}`}>
                    <Typography variant="body2" fontWeight="medium">
                      Create "{option.topic}"
                    </Typography>
                  </Box>
                );
              }
              return (
                <Box component="li" {...props} key={option.id}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <Typography variant="body2" noWrap>{option.topic}</Typography>
                    <Typography variant="caption" color="text.secondary">Already exists</Typography>
                  </Box>
                </Box>
              );
            }}
            noOptionsText="Press Enter to create a new child topic"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setMasterListChildTopicEntries([]);
              setMasterListChildTopicInput('');
              setMasterListCreateChildDialog(false);
            }}
            disabled={masterListCreating}
          >
            Cancel
          </Button>
          <Button
            onClick={handleMasterListCreateChild}
            variant="contained"
            disabled={!masterListChildTopicEntries.some((entry) => entry._isNew) || masterListCreating}
          >
            {masterListCreating ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </AdminLayoutWrapper>
  );
}
